import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { authFetch } from "../api";
import Header from "../components/Header";
import PropertyChecklist from "../components/PropertyChecklist";
import ChatComponent from "../components/ChatComponent"; // Chat component for property questions
import IRRModal from "../components/IRRModal";
import MortgageConfigModal from "../components/MortgageConfigModal";
import ClosingCostsModal from "../components/ClosingCostsModal";
import RentalValueAnalysis from "../components/RentalValueAnalysis";
import WhatIfPlayground from "../components/WhatIfPlayground";
import PropertyDetailsComponent from "../components/PropertyDetails";
import PropertyFeatures from "../components/PropertyFeatures";
import ImageGallery from "../components/ImageGallery";
import ListingEditModal from "../components/ListingEditModal";
import { getMortgageConfig } from "../utils/mortgageRatesApi";
import { calculateDefaultClosingCosts as calculateDefaultClosingCostsUtil } from "../utils/closingCostsCalculator";
import { calculateCapRate, calculateRentalAnalysis, calculateRentFromMode, calculateMonthlyPI, calculateTotalOutOfPocket } from "../utils/rentalAnalysisCalculator";
import { getListingTypeLabel } from "../utils/listingType";
import CurrencyInput from "../components/CurrencyInput";

// Add spinner animation
const spinnerStyle = document.createElement("style");
spinnerStyle.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

// Convert a raw YouTube/Vimeo/Matterport/Kuula URL into an iframe-embeddable URL.
// Returns the original URL if it already looks like an embed or we can't parse it.
const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    const ytWatch = url.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/);
    if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;
    const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    // Matterport + Kuula URLs are already iframe-friendly
    return url;
  } catch (e) {
    return url;
  }
};

// Try to get a static thumbnail for a video. Currently only YouTube exposes one
// without an API call; everything else gets a gradient placeholder on render.
const getVideoThumbnail = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};

export default function PredictionPage() {
  const navigate = useNavigate();

  // Redirect if not logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  const { id } = useParams();
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentLoading, setAgentLoading] = useState(false);
  const [aiPrediction, setAiPrediction] = useState(null);
  const [compScoringData, setCompScoringData] = useState(null);
  const [editCompListing, setEditCompListing] = useState(null);
  const [minCompScore, setMinCompScore] = useState(50);
  const [minCompRent, setMinCompRent] = useState("");
  const [compBedroomsOverride, setCompBedroomsOverride] = useState("");
  const [compMinSqft, setCompMinSqft] = useState("");
  const [compMaxSqft, setCompMaxSqft] = useState("");
  const [rerunMsg, setRerunMsg] = useState(null);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showNeighborhoodTooltip, setShowNeighborhoodTooltip] = useState(false);

  // AI run history — lazy-loaded (only fetched when the modal is first opened, to save page load)
  const [aiHistory, setAiHistory] = useState(null);   // null = not yet loaded
  const [aiHistoryOpen, setAiHistoryOpen] = useState(false);
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);
  const openAiHistory = async () => {
    setAiHistoryOpen(true);
    if (aiHistory !== null) return;                    // already loaded → reuse
    setAiHistoryLoading(true);
    try {
      const data = await authFetch(`/predict/sale/${id}/ai-history`);
      setAiHistory(data?.history || []);
    } catch {
      setAiHistory([]);
    } finally {
      setAiHistoryLoading(false);
    }
  };

  // Open the listing edit modal (shared by the top-bar pencil + the details box)
  const openListingEdit = async () => {
    try {
      const res = await authFetch(`/listings/data-quality?listing_id=${id}`);
      const listing = res.listings?.[0];
      if (listing) setEditCompListing(listing);
    } catch (err) {
      console.error("Failed to load listing:", err);
    }
  };

  // State for Property Analysis (Out of Pocket)
  const [propertyAnalysis, setPropertyAnalysis] = useState({
    down_payment_percent: 25,
    renovation_cost: 0,
    time_to_renovate: 0,
    net_closing_costs: 0
  });
  // True once the saved analysis has been loaded from the DB. The closing-costs
  // recalc effect must not run before this, or it would save the default
  // (renovation_cost: 0, time_to_renovate: 0) back over the persisted values.
  const [analysisLoaded, setAnalysisLoaded] = useState(false);

  // State to track checklist data (for custom rent values)
  const [checklistData, setChecklistData] = useState(null);

  // State for Image Modal
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);

  // State for IRR Modal
  const [isIRRModalOpen, setIsIRRModalOpen] = useState(false);

  // State for global mortgage config (rates for P&I and I/O)
  const [globalMortgageConfig, setGlobalMortgageConfig] = useState(null);

  // Fetch global mortgage config on mount
  useEffect(() => {
    const fetchMortgageConfig = async () => {
      try {
        const config = await getMortgageConfig();
        setGlobalMortgageConfig(config?.data || config);
      } catch (err) {
        console.error("Error fetching mortgage config:", err);
      }
    };
    fetchMortgageConfig();
  }, []);

  const openImageModal = (imageUrl, label = null, imageIndex = 0, imagesArray = []) => {
    // Prefer the full item object from the gallery (has type/originalUrl).
    // Fall back to the legacy {url, label} shape for older call sites.
    const item = imagesArray[imageIndex];
    setSelectedImage(item || { url: imageUrl, label });
    setCurrentImageIndex(imageIndex);
    setAllImages(imagesArray);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setCurrentImageIndex(0);
    setAllImages([]);
  };

  const navigateImage = (direction) => {
    const newIndex = currentImageIndex + direction;
    if (newIndex >= 0 && newIndex < allImages.length) {
      setCurrentImageIndex(newIndex);
      setSelectedImage(allImages[newIndex]);
    }
  };

  // Keyboard navigation for image modal
  useEffect(() => {
    if (!imageModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const newIndex = currentImageIndex - 1;
        if (newIndex >= 0 && newIndex < allImages.length) {
          setCurrentImageIndex(newIndex);
          setSelectedImage(allImages[newIndex]);
        }
      } else if (e.key === 'ArrowRight') {
        const newIndex = currentImageIndex + 1;
        if (newIndex >= 0 && newIndex < allImages.length) {
          setCurrentImageIndex(newIndex);
          setSelectedImage(allImages[newIndex]);
        }
      } else if (e.key === 'Escape') {
        closeImageModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageModalOpen, currentImageIndex, allImages]);

  useEffect(() => {
    let cancelled = false;

    const fetchPrediction = async () => {
      console.log("🔵 [START] Fetching prediction for property:", id);
      setLoading(true);
      setError(null);
      try {
        if (cancelled) return;
        const startTime = performance.now();
        const data = await authFetch(`/predict/sale/${id}`);
        const predictTime = performance.now() - startTime;
        console.log(`✅ [PREDICT] Received prediction in ${predictTime.toFixed(0)}ms`);
        console.log(`📊 [PREDICT] Has agent_analysis:`, !!data.agent_analysis);

        if (cancelled) return;

        setPrediction(data);
        setLoading(false);

        // Extract comp scoring data from prediction response
        if (data.comp_scoring) {
          setCompScoringData(data.comp_scoring);
          if (data.comp_scoring.ai_prediction) {
            setAiPrediction(data.comp_scoring.ai_prediction);
          }
        }

        // If no AI prediction cached, fetch it async
        if (!data.comp_scoring?.ai_prediction && !cancelled) {
          console.log("🤖 [AI] Starting async AI analysis...");
          setAgentLoading(true);
          try {
            const aiData = await authFetch(`/predict/sale/${id}/ai-analysis`, { method: "POST" });
            if (cancelled) return;

            if (aiData.ai_prediction) {
              setAiPrediction(aiData.ai_prediction);
              // Also set as agent_analysis for backward compatibility
              setPrediction((prev) => ({
                ...prev,
                agent_analysis: {
                  agent_adjusted_rent: aiData.ai_prediction.predicted_rent,
                  confidence: aiData.ai_prediction.confidence,
                  reasoning: aiData.ai_prediction.reasoning,
                  key_factors: aiData.ai_prediction.key_factors,
                },
              }));
            }
          } catch (aiErr) {
            console.error("❌ [AI] Error fetching AI analysis:", aiErr);
          } finally {
            if (!cancelled) {
              setAgentLoading(false);
            }
          }
        } else if (data.comp_scoring?.ai_prediction) {
          // Set backward-compatible agent_analysis from cached AI prediction
          const cached = data.comp_scoring.ai_prediction;
          setPrediction((prev) => ({
            ...prev,
            agent_analysis: prev.agent_analysis || {
              agent_adjusted_rent: cached.predicted_rent,
              confidence: cached.confidence,
              reasoning: cached.reasoning,
              key_factors: cached.key_factors,
            },
          }));
        }
      } catch (err) {
        console.error("❌ [PREDICT] Error fetching prediction:", err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchPrediction();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Calculate default net closing costs using shared utility
  const calculateDefaultClosingCosts = async (propertyId, askingPrice, buildingType) => {
    console.log('🔵 calculateDefaultClosingCosts called', { propertyId, askingPrice, buildingType });
    return await calculateDefaultClosingCostsUtil(propertyId, askingPrice, buildingType, authFetch);
  };

  // Load property analysis on mount
  useEffect(() => {
    const loadPropertyAnalysis = async () => {
      console.log('🟢 loadPropertyAnalysis called, prediction.property.id:', prediction?.property?.id);
      if (!prediction?.property?.id) {
        console.log('⚠️ No property ID, skipping');
        return;
      }

      try {
        console.log('📡 Fetching property analysis for:', prediction.property.id);
        const response = await authFetch(`/listings/${prediction.property.id}/analysis`);
        console.log('📨 Analysis response:', response);

        // If a saved analysis exists, always keep it (including renovation_cost /
        // time_to_renovate). Only recompute net_closing_costs when it's missing or 0
        // — don't throw the whole blob away, which used to wipe the reno fields.
        if (response && response.analysis) {
          const saved = response.analysis;
          let netClosingCosts = saved.net_closing_costs;
          if (!(typeof netClosingCosts === 'number' && netClosingCosts > 0)) {
            console.log('⚡ Saved analysis has no net_closing_costs, recomputing that field only...');
            const askingPrice = prediction?.property?.asking_price || prediction?.property?.price || 0;
            const buildingType = prediction?.property?.building_type || "Condo";
            netClosingCosts = await calculateDefaultClosingCosts(
              prediction.property.id,
              askingPrice,
              buildingType
            );
          } else {
            console.log('✅ Using saved analysis with net_closing_costs:', netClosingCosts);
          }
          setPropertyAnalysis({ ...saved, net_closing_costs: netClosingCosts });
        } else {
          console.log('⚡ No saved analysis, calculating defaults...');
          // Calculate default net closing costs if none exist
          const askingPrice = prediction?.property?.asking_price || prediction?.property?.price || 0;
          const buildingType = prediction?.property?.building_type || "Condo";
          const defaultClosingCosts = await calculateDefaultClosingCosts(
            prediction.property.id,
            askingPrice,
            buildingType
          );

          console.log('Calculated default closing costs:', defaultClosingCosts);

          setPropertyAnalysis({
            down_payment_percent: 25,
            renovation_cost: 0,
            time_to_renovate: 0,
            net_closing_costs: defaultClosingCosts
          });
        }
        setAnalysisLoaded(true);
      } catch (error) {
        console.log('❌ Error loading property analysis, calculating defaults');
        console.error(error);
        // Calculate default net closing costs
        const askingPrice = prediction?.property?.asking_price || prediction?.property?.price || 0;
        const buildingType = prediction?.property?.building_type || "Condo";
        const defaultClosingCosts = await calculateDefaultClosingCosts(
          prediction.property.id,
          askingPrice,
          buildingType
        );

        setPropertyAnalysis({
          down_payment_percent: 25,
          renovation_cost: 0,
          time_to_renovate: 0,
          net_closing_costs: defaultClosingCosts
        });
        setAnalysisLoaded(true);
      }
    };

    if (prediction?.property?.id) {
      loadPropertyAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prediction?.property?.id]);

  // Recalculate closing costs when checklist other_costs_items change
  useEffect(() => {
    const recalculateClosingCosts = async () => {
      // Wait for the saved analysis to load first, otherwise we'd persist the
      // default renovation_cost/time_to_renovate over the saved values.
      if (!prediction?.property?.id || !propertyAnalysis || !analysisLoaded) return;

      // Filter items by payment type
      const allCostItems = checklistData?.other_costs_items || [];
      const closingCostsItems = allCostItems.filter(item => item.payment_type === "closing_costs" && item.name && item.cost);
      const financedItems = allCostItems.filter(item => item.payment_type === "financed" && item.name && item.cost);

      // Import the calculateClosingCosts function
      const { calculateClosingCosts } = await import('../utils/closingCostsCalculator');

      // Get config
      const response = await authFetch("/config/closing_costs");
      const configData = response.data;

      // Calculate adjusted asking price
      const originalPrice = prediction.property.price || prediction.property.asking_price || 0;
      const discountPct = checklistData?.discount_pct ? parseFloat(checklistData.discount_pct) : 0;
      let askingPrice = originalPrice * (1 - discountPct / 100);

      // Add financed costs to asking price
      const financedCostsTotal = financedItems.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
      askingPrice += financedCostsTotal;

      // Get saved manual "Other" costs from property analysis (these are user-added costs from the modal)
      const savedOtherCosts = propertyAnalysis.other_costs || [];

      // Filter to only keep user-added costs (ID < 100), not checklist-synced items
      const manualOtherCosts = savedOtherCosts.filter(item => item.id < 100);

      // Convert checklist closing_costs items to otherCosts format
      const checklistOtherCosts = closingCostsItems.map((item, index) => ({
        id: item.id || index + 100,
        label: item.name,
        amount: parseFloat(item.cost) || 0
      }));

      // Add assessment with end date as a closing cost (dynamic total based on remaining months)
      const assessmentClosingCosts = [];
      if (checklistData?.assessment_enabled && checklistData?.assessment_type === "monthly" && checklistData?.assessment_end_date && checklistData?.assessment_cost) {
        const monthlyCost = parseFloat(checklistData.assessment_cost) || 0;
        const remainingMonths = Math.max(0, Math.ceil((new Date(checklistData.assessment_end_date) - new Date()) / (1000 * 60 * 60 * 24 * 30.44)));
        const totalCost = monthlyCost * remainingMonths;
        const creditPct = checklistData.assessment_credit_pct ? parseFloat(checklistData.assessment_credit_pct) : 0;
        const effectiveTotal = Math.round(totalCost * (1 - creditPct / 100));
        if (effectiveTotal > 0) {
          assessmentClosingCosts.push({
            id: 200,
            label: `Assessment ($${monthlyCost.toLocaleString()}/mo × ${remainingMonths}mo${creditPct ? `, ${creditPct}% credit` : ''})`,
            amount: effectiveTotal
          });
        }
      }

      // Merge manual costs with checklist costs and assessment
      const otherCosts = [...manualOtherCosts, ...checklistOtherCosts, ...assessmentClosingCosts];

      // Calculate with included items
      const result = calculateClosingCosts({
        askingPrice,
        buildingType: propertyAnalysis.building_type || prediction.property.building_type || "Condo",
        saleType: propertyAnalysis.sale_type || "Resale",
        financing: propertyAnalysis.financing || "Yes",
        config: configData,
        editableFees: propertyAnalysis.editable_fees || {},
        otherCosts,
        closingCostCredit: propertyAnalysis.closing_cost_credit || 0,
        monthlyHoa: prediction?.property?.monthly_hoa || 0 // Sponsor working-capital default = HOA × 2
      });

      // Update property analysis with new closing costs
      const updatedAnalysis = {
        ...propertyAnalysis,
        net_closing_costs: result.netClosingCosts
      };

      savePropertyAnalysis(updatedAnalysis);
    };

    recalculateClosingCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisLoaded, checklistData?.other_costs_items, checklistData?.assessment_enabled, checklistData?.assessment_type, checklistData?.assessment_cost, checklistData?.assessment_end_date, checklistData?.assessment_credit_pct, prediction?.property?.id, propertyAnalysis?.id]);

  // Save property analysis to DB
  const savePropertyAnalysis = async (analysisData) => {
    if (!prediction?.property?.id) return;

    try {
      await authFetch(`/listings/${prediction.property.id}/analysis`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(analysisData),
      });
      setPropertyAnalysis(analysisData);
      console.log('Property analysis saved:', analysisData);
    } catch (error) {
      console.error('Error saving property analysis:', error);
    }
  };

  // Handle ClosingCostsModal save
  const handleClosingCostsSave = (data) => {
    const updatedAnalysis = {
      ...propertyAnalysis,
      net_closing_costs: data.netClosingCosts,
      sale_type: data.saleType,
      building_type: data.buildingType,
      financing: data.financing,
      editable_fees: data.editableFees,
      closing_cost_credit: data.closingCostCredit,
      other_costs: data.otherCosts
    };
    savePropertyAnalysis(updatedAnalysis);
  };

  // Handle MortgageConfigModal save
  const handleMortgageSave = (config) => {
    console.log("💾 Saving mortgage config:", config);
    const updatedAnalysis = {
      ...propertyAnalysis,
      down_payment_percent: config.downPaymentPercent,
      down_payment_amount: config.downPaymentAmount,
      mortgage_term: config.mortgageTerm,
      payment_type: config.paymentType,
      mortgage_rate: config.mortgageRate,
      loan_amount: config.loanAmount,
      monthly_payment: config.monthlyPayment
    };
    console.log("💾 Updated analysis being saved:", updatedAnalysis);
    savePropertyAnalysis(updatedAnalysis);
    console.log("Mortgage configuration saved to DB:", config);
  };

  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    display: "flex",
    flexDirection: "column",
  };

  const mainContentStyle = {
    flex: 1,
    padding: "30px",
    maxWidth: "1400px",
    margin: "0 auto",
    width: "100%",
  };

  const twoColumnLayoutStyle = {
    display: "grid",
    gridTemplateColumns: "60% 40%",
    gap: "30px",
    marginTop: "20px",
  };

  const leftColumnStyle = {
    minWidth: 0, // Prevents grid overflow
  };

  const rightColumnStyle = {
    minWidth: 0, // Prevents grid overflow
    // Pin the whole right column so the form + map stick together and the map
    // (last child) never gets scrolled over by the sticky form.
    position: "sticky",
    top: "20px",
    maxHeight: "calc(100vh - 60px)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  };

  const handleChecklistSave = (checklist) => {
    // Update checklist state so IRRModal can use custom rent values
    console.log("Checklist update:", checklist);
    setChecklistData(checklist);
  };

  // ===================================================================
  // CENTRALIZED STATE MANAGEMENT
  // ===================================================================
  // This useMemo block is the SINGLE SOURCE OF TRUTH for all derived values.
  // All rent calculations respect the user's radio button selections.
  //
  // Key principles:
  // 1. Radio button mode determines which rent value is used (ml/ai/current_tenant/custom)
  // 2. All child components receive computed values from this parent state
  // 3. Values are recalculated only when dependencies change (performance optimization)
  // 4. No child component should duplicate these calculations
  //
  // NOTE: This must be called before any conditional returns (React hooks rules)
  // ===================================================================
  const computedValues = useMemo(() => {
    // Safe to call even when prediction is null (will return zeros)
    if (!prediction?.property || !prediction?.prediction) {
      return {
        mlRent: 0, agentRent: 0, tenantRent: 0,
        customLowRent: 0, customHighRent: 0,
        lowRent: 0, highRent: 0,
        originalPrice: 0, discountPct: 0, discountedPrice: 0,
        monthlyTax: 0, monthlyHOA: 0, monthlyCarryingCosts: 0,
        mortgageRate: 7, paymentType: "P&I", downPaymentPercent: 25,
        downPayment: 0, loanAmount: 0, monthlyMortgagePayment: 0, totalOutOfPocket: 0
      };
    }

    const { property, prediction: pred, agent_analysis } = prediction;

    // Raw rent values from different sources
    const mlRent = pred?.predicted_rent || 0;
    const agentRent = aiPrediction?.predicted_rent || agent_analysis?.agent_adjusted_rent || 0;
    const tenantRent = checklistData?.tenant_current_rent ? parseFloat(checklistData.tenant_current_rent) : 0;

    // Custom rent values from checklist
    const customLowRent = checklistData?.rent_prediction_range_low ? parseFloat(checklistData.rent_prediction_range_low) : 0;
    const customHighRent = checklistData?.rent_prediction_range_high ? parseFloat(checklistData.rent_prediction_range_high) : 0;

    // Compute rent based on radio button selection using helper function
    // Default to ML rent if no mode is selected (so cap rate displays on first load)
    const lowRent = calculateRentFromMode(
      checklistData?.rent_prediction_mode_low,
      { ml: mlRent, ai: agentRent, tenant: tenantRent, custom: customLowRent }
    ) || mlRent;

    const highRent = calculateRentFromMode(
      checklistData?.rent_prediction_mode_high,
      { ml: mlRent, ai: agentRent, tenant: tenantRent, custom: customHighRent }
    ) || mlRent;

    // Price calculations (single source of truth)
    const originalPrice = property?.price || 0;
    const discountPct = checklistData?.discount_pct ? parseFloat(checklistData.discount_pct) : 0;
    let discountedPrice = originalPrice * (1 - discountPct / 100);

    // Add financed other costs to the asking price
    const otherCostsItems = checklistData?.other_costs_items || [];
    const financedCosts = otherCostsItems
      .filter(item => item.payment_type === "financed" && item.cost)
      .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
    discountedPrice += financedCosts;

    // Carrying costs (single source of truth)
    const monthlyTax = property?.monthly_tax || 0;
    const monthlyHOA = property?.monthly_hoa || 0;
    // Only "forever" assessments add to carrying costs (affects cap rate)
    // "monthly" with end date goes to closing costs instead
    const assessmentEnabled = checklistData?.assessment_enabled;
    const assessmentType = checklistData?.assessment_type;
    const assessmentCost = checklistData?.assessment_cost ? parseFloat(checklistData.assessment_cost) : 0;
    const monthlyAssessment = assessmentEnabled && assessmentCost > 0 && assessmentType === "forever" ? assessmentCost : 0;
    const monthlyCarryingCosts = monthlyTax + monthlyHOA + monthlyAssessment;

    // Mortgage calculations (used for IRR and financial analysis)
    const mortgageRate = propertyAnalysis?.mortgage_rate || 7;
    const paymentType = propertyAnalysis?.payment_type || "P&I";
    const downPaymentPercent = propertyAnalysis?.down_payment_percent || 25;
    const downPayment = discountedPrice * (downPaymentPercent / 100);
    const loanAmount = discountedPrice - downPayment;

    // Monthly mortgage payment (use saved value if available, otherwise calculate)
    const monthlyMortgagePayment = propertyAnalysis?.monthly_payment
      ? propertyAnalysis.monthly_payment
      : calculateMonthlyPI(loanAmount, mortgageRate, 30);

    // Total out of pocket (for IRR calculations)
    const totalOutOfPocket = calculateTotalOutOfPocket(
      downPayment,
      propertyAnalysis?.net_closing_costs || 0,
      propertyAnalysis?.renovation_cost || 0,
      propertyAnalysis?.time_to_renovate || 0,
      monthlyCarryingCosts,
      monthlyMortgagePayment
    );

    console.log("✅ Computed values:", {
      mlRent, agentRent, tenantRent,
      lowRent, highRent,
      originalPrice, discountPct, discountedPrice,
      monthlyTax, monthlyHOA, monthlyCarryingCosts,
      mortgageRate, paymentType, downPaymentPercent,
      downPayment, loanAmount, monthlyMortgagePayment, totalOutOfPocket
    });

    return {
      // Raw rent values
      mlRent,
      agentRent,
      tenantRent,
      customLowRent,
      customHighRent,

      // Computed rents based on radio button selection
      lowRent,
      highRent,

      // Price values
      originalPrice,
      discountPct,
      discountedPrice,

      // Carrying costs
      monthlyTax,
      monthlyHOA,
      monthlyAssessment,
      monthlyCarryingCosts,

      // Mortgage values (for IRR and financial analysis)
      mortgageRate,
      paymentType,
      downPaymentPercent,
      downPayment,
      loanAmount,
      monthlyMortgagePayment,
      totalOutOfPocket
    };
  }, [prediction, checklistData, propertyAnalysis, aiPrediction]);

  const loadingStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    color: "#666",
  };

  const errorStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    color: "#dc3545",
  };

  if (loading) {
    console.log("⏳ [LOADING] Still showing loading screen...");
    return (
      <div style={containerStyle}>
        <Header showLogout={true} />
        <div style={loadingStyle}>Loading prediction...</div>
      </div>
    );
  }

  console.log("✅ [LOADED] Rendering main content. Has prediction:", !!prediction, "Agent loading:", agentLoading);

  if (error) return (
    <div style={containerStyle}>
      <Header showLogout={true} />
      <div style={errorStyle}>Error: {error}</div>
    </div>
  );

  if (!prediction) return (
    <div style={containerStyle}>
      <Header showLogout={true} />
      <div style={loadingStyle}>No prediction data available</div>
    </div>
  );

  // Destructure prediction data after we know it exists
  const { property, prediction: pred, same_building_analysis, agent_analysis, cache_info } = prediction;

  // Handle case where property data is missing (e.g., old database schema)
  if (!property) return (
    <div style={containerStyle}>
      <Header showLogout={true} />
      <div style={loadingStyle}>Property data not available for this listing</div>
    </div>
  );

  return (
    <div style={containerStyle}>
      <Header showLogout={true} />

      <div style={mainContentStyle}>
        <div style={twoColumnLayoutStyle}>
          {/* Left Column - Main Content */}
          <div style={leftColumnStyle}>
            {/* Compact key-details bar above the photos: address+unit · condo/co-op · status · price */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px",
                          background: "#fff", border: "1px solid #000", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span
                  onClick={openListingEdit}
                  title="Edit listing"
                  style={{ cursor: "pointer", fontSize: "16px", opacity: 0.5, lineHeight: 1, display: "flex", alignItems: "center" }}
                >&#9998;</span>
                <span style={{ fontSize: "20px", fontWeight: 700, color: "#111" }}>
                  {property.address}{property.unit_number ? `, ${property.unit_number}` : ""}
                </span>
                {property.verified && (
                  <span title="Data verified" style={{ fontSize: "11px", fontWeight: 700, color: "#2e7d32",
                                 background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: "4px", padding: "2px 8px" }}>
                    ✓ Verified
                  </span>
                )}
                {property.building_type && (
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#555", border: "1px solid #ddd", borderRadius: "4px", padding: "2px 8px" }}>
                    {property.building_type}
                  </span>
                )}
                {(() => {
                  const lt = getListingTypeLabel(property.listing_type_code);
                  return lt && (
                    <span title={lt.label} style={{ fontSize: "12px", fontWeight: 700, color: lt.color, background: lt.bg, border: `1px solid ${lt.border}`, borderRadius: "4px", padding: "2px 8px" }}>
                      {lt.label}
                    </span>
                  );
                })()}
                {(property.listing_url || property.streeteasy_url) && (
                  <a
                    href={property.listing_url || property.streeteasy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View web listing"
                    style={{ display: "flex", alignItems: "center", color: "#1976d2", textDecoration: "none", fontSize: "16px" }}
                  >🔗</a>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {property.listing_status && (
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#0d47a1", background: "#e3f0ff",
                                 borderRadius: "12px", padding: "4px 12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {property.listing_status}
                  </span>
                )}
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "22px", fontWeight: 700, color: "#111", whiteSpace: "nowrap" }}>
                    {(property.price || property.asking_price)
                      ? `$${Number(property.price || property.asking_price).toLocaleString()}`
                      : "—"}
                  </span>
                  {property.price_change != null && property.price_change !== 0 && (
                    <div style={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
                                  color: property.price_change < 0 ? "#dc3545" : "#28a745" }}>
                      {property.price_change < 0 ? "▼" : "▲"} ${Math.abs(property.price_change).toLocaleString()}
                      {property.price_change_pct != null
                        ? ` (${property.price_change_pct > 0 ? "+" : ""}${property.price_change_pct.toFixed(1)}%)`
                        : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Property Media Gallery - Scrollable Strip (Floor Plan, Videos, Tours, Images) */}
            {(property?.image_urls?.length > 0 || property?.floor_plan_url || property?.video_urls?.length > 0 || property?.tour_urls?.length > 0) && (() => {
              // Build combined gallery array. Order: floor plan → videos → tours → images.
              const galleryItems = [];
              if (property.floor_plan_url) {
                galleryItems.push({ url: property.floor_plan_url, label: "Floor Plan", type: "floor_plan" });
              }
              (property.video_urls || []).forEach((url, idx) => {
                galleryItems.push({
                  url: toEmbedUrl(url),
                  originalUrl: url,
                  label: `Video${property.video_urls.length > 1 ? " " + (idx + 1) : ""}`,
                  type: "video",
                });
              });
              (property.tour_urls || []).forEach((url, idx) => {
                galleryItems.push({
                  url: toEmbedUrl(url),
                  originalUrl: url,
                  label: `3D Tour${property.tour_urls.length > 1 ? " " + (idx + 1) : ""}`,
                  type: "tour",
                });
              });
              (property.image_urls || []).forEach((url, idx) => {
                galleryItems.push({ url, label: `Image ${idx + 1}`, type: "image" });
              });

              const renderTile = (item, index) => {
                const isVideo = item.type === "video";
                const isTour = item.type === "tour";
                const isFloorPlan = item.type === "floor_plan";
                const isImage = item.type === "image";
                const ytThumb = isVideo ? getVideoThumbnail(item.originalUrl) : null;

                const commonStyle = {
                  minWidth: "250px",
                  height: "200px",
                  position: "relative",
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                };

                if (isFloorPlan) {
                  return (
                    <div key={`fp-${index}`} style={{ ...commonStyle, border: "2px solid #4CAF50" }}>
                      <div style={{
                        width: "100%", height: "100%",
                        backgroundImage: `url(${item.url})`,
                        backgroundSize: "cover", backgroundPosition: "center",
                      }} onClick={() => openImageModal(item.url, item.label, index, galleryItems)} />
                      <div style={{
                        position: "absolute", top: "8px", left: "8px",
                        backgroundColor: "#4CAF50", color: "white",
                        padding: "4px 8px", borderRadius: "4px",
                        fontSize: "12px", fontWeight: "600",
                      }}>
                        Floor Plan
                      </div>
                    </div>
                  );
                }

                if (isVideo || isTour) {
                  const bg = ytThumb
                    ? `url(${ytThumb}) center/cover`
                    : isTour
                      ? "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)"
                      : "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";
                  const badgeColor = isTour ? "#6a11cb" : "#e53935";
                  return (
                    <div
                      key={`${item.type}-${index}`}
                      style={{ ...commonStyle, background: bg, border: `2px solid ${badgeColor}` }}
                      onClick={() => openImageModal(item.url, item.label, index, galleryItems)}
                    >
                      {/* Play icon overlay */}
                      <div style={{
                        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        backgroundColor: ytThumb ? "rgba(0,0,0,0.3)" : "transparent",
                      }}>
                        <div style={{
                          width: "60px", height: "60px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.95)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "24px", color: "#000",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                        }}>
                          ▶
                        </div>
                      </div>
                      <div style={{
                        position: "absolute", top: "8px", left: "8px",
                        backgroundColor: badgeColor, color: "white",
                        padding: "4px 8px", borderRadius: "4px",
                        fontSize: "12px", fontWeight: "600",
                      }}>
                        {item.label}
                      </div>
                    </div>
                  );
                }

                // Regular image
                return (
                  <div
                    key={`img-${index}`}
                    style={{
                      ...commonStyle,
                      backgroundImage: `url(${item.url})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                    onClick={() => openImageModal(item.url, item.label, index, galleryItems)}
                  />
                );
              };

              return (
                <div style={{ marginBottom: "24px", position: "relative" }}>
                  <div style={{
                    display: "flex",
                    gap: "12px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "12px",
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#888 #f1f1f1",
                  }}>
                    {galleryItems.map((item, index) => renderTile(item, index))}
                  </div>

                  {/* Scrollbar styling for webkit browsers */}
                  <style>{`
                    div::-webkit-scrollbar {
                      height: 8px;
                    }
                    div::-webkit-scrollbar-track {
                      background: #f1f1f1;
                      border-radius: 4px;
                    }
                    div::-webkit-scrollbar-thumb {
                      background: #888;
                      border-radius: 4px;
                    }
                    div::-webkit-scrollbar-thumb:hover {
                      background: #555;
                    }
                  `}</style>
                </div>
              );
            })()}

            <PropertyDetails
              property={property}
              prediction={pred}
              propertyAnalysis={propertyAnalysis}
              checklistData={checklistData}
              agentAnalysis={agent_analysis}
              computedValues={computedValues}
              onEdit={async () => {
                try {
                  const res = await authFetch(`/listings/data-quality?listing_id=${id}`);
                  const listing = res.listings?.[0];
                  if (listing) setEditCompListing(listing);
                } catch (err) {
                  console.error("Failed to load listing:", err);
                }
              }}
            />

            <PropertyFeatures property={property} />

            <PriceHistory listingId={id} />

            <PredictionResults
              prediction={pred}
              property={property}
              agentAnalysis={agent_analysis}
              handleClosingCostsSave={handleClosingCostsSave}
              handleMortgageSave={handleMortgageSave}
              propertyAnalysis={propertyAnalysis}
              checklistData={checklistData}
              computedValues={computedValues}
              imageModalOpen={imageModalOpen}
              selectedImage={selectedImage}
              closeImageModal={closeImageModal}
              navigateImage={navigateImage}
              currentImageIndex={currentImageIndex}
              allImages={allImages}
              openIRRModal={() => setIsIRRModalOpen(true)}
            />

            <WhatIfPlayground
              agentRent={computedValues.agentRent}
              monthlyTax={computedValues.monthlyTax}
              monthlyHOA={computedValues.monthlyHOA}
              sqft={property?.sqft}
              askingPrice={property?.price || property?.asking_price}
            />

            <RentalValueAnalysis
              property={property}
              prediction={pred}
              propertyAnalysis={propertyAnalysis}
              mlRent={computedValues.mlRent}
              agentRent={computedValues.agentRent}
              lowRent={computedValues.lowRent}
              highRent={computedValues.highRent}
              discountedPrice={computedValues.discountedPrice}
              mortgageConfig={globalMortgageConfig}
              tenantRent={checklistData?.tenant_current_rent ? parseFloat(checklistData.tenant_current_rent) : null}
              monthlyAssessment={computedValues.monthlyAssessment}
            />

            {same_building_analysis && <SameBuildingAnalysis analysis={same_building_analysis} />}

            <OutOfPocketSection
              property={property}
              prediction={pred}
              propertyAnalysis={propertyAnalysis}
              checklistData={checklistData}
              onUpdate={savePropertyAnalysis}
            />

            {agentLoading ? (
              <div style={{
                backgroundColor: "white",
                border: "none",
                borderRadius: "8px",
                padding: "40px",
                marginBottom: "24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                textAlign: "center"
              }}>
                <div style={{
                  display: "inline-block",
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #000",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                <div style={{ marginTop: "16px", color: "#666" }}>Loading AI analysis...</div>
              </div>
            ) : aiPrediction ? (
              <AIPredictionAnalysis
                aiPrediction={aiPrediction}
                comps={compScoringData?.comps}
                tenantCurrentRent={checklistData?.tenant_current_rent}
                propertyId={property?.id}
                predictionId={pred?.id}
                listingId={id}
                minCompScore={minCompScore}
                setMinCompScore={setMinCompScore}
                minCompRent={minCompRent}
                setMinCompRent={setMinCompRent}
                compBedroomsOverride={compBedroomsOverride}
                setCompBedroomsOverride={setCompBedroomsOverride}
                compMinSqft={compMinSqft}
                setCompMinSqft={setCompMinSqft}
                compMaxSqft={compMaxSqft}
                setCompMaxSqft={setCompMaxSqft}
                targetBedrooms={property?.bedrooms}
                onRerun={async () => {
                  setAgentLoading(true);
                  setRerunMsg(null);
                  try {
                    // Custom rent anchor: if the analyst picked "Custom" for the rent
                    // prediction range, feed that value to the AI as a mandatory anchor
                    // (same effect as telling it the rent in chat). Single value — the
                    // side edited most recently wins when both sides are custom.
                    const cLow = checklistData?.rent_prediction_mode_low === "custom"
                      ? parseFloat(checklistData?.rent_prediction_range_low) : NaN;
                    const cHigh = checklistData?.rent_prediction_mode_high === "custom"
                      ? parseFloat(checklistData?.rent_prediction_range_high) : NaN;
                    const lowActive = cLow > 0;
                    const highActive = cHigh > 0;
                    let userRent = null;
                    if (lowActive && highActive) {
                      userRent = checklistData?.rent_prediction_custom_last_edited === "low" ? cLow : cHigh;
                    } else if (lowActive) userRent = cLow;
                    else if (highActive) userRent = cHigh;
                    const userRentParam = userRent ? `&user_rent=${Math.round(userRent)}` : "";
                    // Step 1: Refresh comps with filters (backend anchors to today).
                    // Use the SAME params as the AI call below (min_score, NOT target_score)
                    // so the two generate the identical comp set — otherwise the two saves
                    // clobber each other and 'changed' is always true.
                    const body = {};
                    if (minCompScore) body.min_score = parseInt(minCompScore);
                    if (compBedroomsOverride) body.bedroom_override = parseInt(compBedroomsOverride);
                    else if (checklistData?.convertible_enabled && checklistData?.convertible_bedrooms) body.bedroom_override = parseInt(checklistData.convertible_bedrooms);
                    if (compMinSqft) body.min_sqft = parseInt(compMinSqft);
                    if (compMaxSqft) body.max_sqft = parseInt(compMaxSqft);
                    if (minCompRent) body.min_rent = parseInt(minCompRent);
                    const refreshed = await authFetch(`/comp-scoring/${id}/refresh`, { method: "POST", body: JSON.stringify(body) });
                    // If the comp set is identical to what's cached, nothing about the
                    // analysis would change — skip the expensive AI re-run (Bedrock call).
                    // Exception: a custom rent anchor must always re-run the AI, since the
                    // anchor changes the result even when the comps don't.
                    if (refreshed && refreshed.changed === false && aiPrediction && !userRent) {
                      if (refreshed.comps) setCompScoringData(refreshed);
                      setRerunMsg("No change — comps and analysis are current.");
                      setAgentLoading(false);
                      return;
                    }
                    // Step 2: Run AI on refreshed comps
                    const brOverride = compBedroomsOverride
                      ? `&bedroom_override=${compBedroomsOverride}`
                      : (checklistData?.convertible_enabled && checklistData?.convertible_bedrooms
                        ? `&bedroom_override=${checklistData.convertible_bedrooms}` : "");
                    const scoreParam = minCompScore !== 50 ? `&min_score=${minCompScore}` : "";
                    const rentParam = minCompRent ? `&min_rent=${minCompRent}` : "";
                    const minSqftParam = compMinSqft ? `&min_sqft=${compMinSqft}` : "";
                    const maxSqftParam = compMaxSqft ? `&max_sqft=${compMaxSqft}` : "";
                    const aiData = await authFetch(`/predict/sale/${id}/ai-analysis?force=true${brOverride}${scoreParam}${rentParam}${minSqftParam}${maxSqftParam}${userRentParam}`, { method: "POST" });
                    if (aiData.ai_prediction) {
                      setAiPrediction(aiData.ai_prediction);
                      setPrediction((prev) => ({
                        ...prev,
                        agent_analysis: {
                          agent_adjusted_rent: aiData.ai_prediction.predicted_rent,
                          confidence: aiData.ai_prediction.confidence,
                          reasoning: aiData.ai_prediction.reasoning,
                          key_factors: aiData.ai_prediction.key_factors,
                        },
                      }));
                    }
                    // Reload prediction to get fresh comp scoring data
                    const data = await authFetch(`/predict/sale/${id}`);
                    if (data.comp_scoring) {
                      setCompScoringData(data.comp_scoring);
                    }
                    setRerunMsg("Comps updated — analysis re-run.");
                  } catch (err) {
                    console.error("Error re-running AI analysis:", err);
                  }
                  setAgentLoading(false);
                }}
                rerunMsg={rerunMsg}
              />
            ) : (
              <div style={{ backgroundColor: "white", borderRadius: "8px", padding: "24px", marginBottom: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#333", margin: 0 }}>AI Rent Prediction</h2>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <select value={minCompScore} onChange={(e) => setMinCompScore(parseInt(e.target.value))}
                      style={{ padding: "5px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc" }}
                      title="Minimum comp similarity score">
                      <option value={70}>Super strict (70+)</option>
                      <option value={50}>Strict (50+)</option>
                      <option value={30}>Relaxed (30+)</option>
                      <option value={0}>All comps</option>
                    </select>
                    <input type="number" value={minCompRent} onChange={(e) => setMinCompRent(e.target.value)}
                      placeholder="Min rent $" title="Minimum comp rent"
                      style={{ padding: "5px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc", width: 90 }} />
                    <input type="number" value={compBedroomsOverride} onChange={(e) => setCompBedroomsOverride(e.target.value)}
                      placeholder={property?.bedrooms != null ? `${property.bedrooms} BR` : "Beds"} title="Override bedrooms for comp matching"
                      style={{ padding: "5px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc", width: 60 }} step="1" min="0" max="5" />
                    <input type="number" value={compMinSqft} onChange={(e) => setCompMinSqft(e.target.value)}
                      placeholder="Min sqft" title="Minimum comp sqft"
                      style={{ padding: "5px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc", width: 80 }} />
                    <input type="number" value={compMaxSqft} onChange={(e) => setCompMaxSqft(e.target.value)}
                      placeholder="Max sqft" title="Maximum comp sqft"
                      style={{ padding: "5px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc", width: 80 }} />
                    <button onClick={async () => {
                      setAgentLoading(true);
                      try {
                        // Custom rent anchor (see onRerun above): feed a Custom rent to
                        // the AI as a mandatory anchor. Last-edited side wins when both set.
                        const cLow = checklistData?.rent_prediction_mode_low === "custom"
                          ? parseFloat(checklistData?.rent_prediction_range_low) : NaN;
                        const cHigh = checklistData?.rent_prediction_mode_high === "custom"
                          ? parseFloat(checklistData?.rent_prediction_range_high) : NaN;
                        let userRent = null;
                        if (cLow > 0 && cHigh > 0) {
                          userRent = checklistData?.rent_prediction_custom_last_edited === "low" ? cLow : cHigh;
                        } else if (cLow > 0) userRent = cLow;
                        else if (cHigh > 0) userRent = cHigh;
                        const userRentParam = userRent ? `&user_rent=${Math.round(userRent)}` : "";
                        // Step 1: Refresh comps with filters
                        const body = { target_score: minCompScore || 700 };
                        if (compBedroomsOverride) body.bedroom_override = parseInt(compBedroomsOverride);
                        else if (checklistData?.convertible_enabled && checklistData?.convertible_bedrooms) body.bedroom_override = parseInt(checklistData.convertible_bedrooms);
                        if (compMinSqft) body.min_sqft = parseInt(compMinSqft);
                        if (compMaxSqft) body.max_sqft = parseInt(compMaxSqft);
                        if (minCompRent) body.min_rent = parseInt(minCompRent);
                        const refreshed = await authFetch(`/comp-scoring/${id}/refresh`, { method: "POST", body: JSON.stringify(body) });
                        if (refreshed) setCompScoringData(refreshed);
                        // Step 2: Run AI prediction on refreshed comps
                        const brOverride = compBedroomsOverride
                          ? `&bedroom_override=${compBedroomsOverride}`
                          : (checklistData?.convertible_enabled && checklistData?.convertible_bedrooms
                            ? `&bedroom_override=${checklistData.convertible_bedrooms}` : "");
                        const scoreParam = minCompScore !== 50 ? `&min_score=${minCompScore}` : "";
                        const rentParam = minCompRent ? `&min_rent=${minCompRent}` : "";
                        const minSqftParam = compMinSqft ? `&min_sqft=${compMinSqft}` : "";
                        const maxSqftParam = compMaxSqft ? `&max_sqft=${compMaxSqft}` : "";
                        const aiData = await authFetch(`/predict/sale/${id}/ai-analysis?force=true${brOverride}${scoreParam}${rentParam}${minSqftParam}${maxSqftParam}${userRentParam}`, { method: "POST" });
                        if (aiData.ai_prediction) setAiPrediction(aiData.ai_prediction);
                        const data = await authFetch(`/predict/sale/${id}`);
                        if (data.comp_scoring) setCompScoringData(data.comp_scoring);
                      } catch (err) { console.error(err); }
                      setAgentLoading(false);
                    }} style={{ padding: "6px 16px", fontSize: 13, fontWeight: 600, borderRadius: 6, border: "1px solid #1976d2", backgroundColor: "#1976d2", color: "#fff", cursor: "pointer" }}>
                      Re-run
                    </button>
                  </div>
                </div>
                <p style={{ color: "#999", fontSize: 13, marginTop: 12 }}>No AI prediction yet. Adjust settings and click "Re-run" to generate.</p>
              </div>
            )}

            {compScoringData?.comps?.length > 0 && (
              <CompScoringSection
                data={compScoringData}
                listingId={id}
                onEditComp={async (compListingId) => {
                  try {
                    const listing = await authFetch(`/listings/${compListingId}/detail`);
                    if (listing) setEditCompListing(listing);
                  } catch (err) {
                    console.error("Failed to load comp listing:", err);
                  }
                }}
                onExcludeComp={async (compListingId, excluded = true) => {
                  try {
                    await authFetch(`/comp-scoring/${id}/exclude`, {
                      method: "POST",
                      body: JSON.stringify({ comp_listing_id: compListingId, excluded }),
                    });
                    // Refresh comp data
                    const data = await authFetch(`/predict/sale/${id}`);
                    if (data.comp_scoring) setCompScoringData(data.comp_scoring);
                  } catch (err) {
                    console.error("Failed to exclude comp:", err);
                  }
                }}
              />
            )}
            <CacheInfo
              cache={cache_info}
              aiGeneratedAt={aiPrediction?.generated_at}
              compsUpdatedAt={compScoringData?.comps_updated_at}
              onShowHistory={openAiHistory}
            />
            {aiHistoryOpen && (
              <AiHistoryModal
                loading={aiHistoryLoading}
                history={aiHistory}
                onClose={() => setAiHistoryOpen(false)}
              />
            )}
          </div>

          {/* Right Column - Checklist */}
          <div style={rightColumnStyle}>
            <PropertyChecklist
              property={property}
              prediction={pred}
              reviewId={property.id}
              onSave={handleChecklistSave}
              agentAnalysis={agent_analysis}
              aiPrediction={aiPrediction}
              computedValues={computedValues}
              onPropertyUpdate={async () => {
                // Reload prediction to get updated property data
                try {
                  const data = await authFetch(`/predict/sale/${id}`);
                  setPrediction((prev) => ({
                    ...data,
                    agent_analysis: data.agent_analysis || prev?.agent_analysis,
                  }));
                  // Refresh comp scoring data
                  if (data.comp_scoring) {
                    setCompScoringData(data.comp_scoring);
                    if (data.comp_scoring.ai_prediction) {
                      setAiPrediction(data.comp_scoring.ai_prediction);
                    }
                  }
                } catch (err) {
                  console.error("Error reloading prediction:", err);
                }
              }}
            />
            <AddressMap property={property} />
          </div>
        </div>
      </div>

      {/* Edit Comp Listing Modal */}
      {editCompListing && (
        <ListingEditModal
          listing={editCompListing}
          onClose={() => setEditCompListing(null)}
          onSave={async (listingId) => {
            setEditCompListing(null);
            // Refresh comp scoring data
            try {
              const data = await authFetch(`/predict/sale/${id}`);
              if (data.comp_scoring) setCompScoringData(data.comp_scoring);
            } catch (err) {
              console.error("Error refreshing comps:", err);
            }
          }}
          onVerify={async (listingId) => {
            setEditCompListing(null);
            try {
              const data = await authFetch(`/predict/sale/${id}`);
              if (data.comp_scoring) setCompScoringData(data.comp_scoring);
            } catch (err) {
              console.error("Error refreshing comps:", err);
            }
          }}
        />
      )}

      {/* IRR Modal */}
      <IRRModal
        isOpen={isIRRModalOpen}
        onClose={() => setIsIRRModalOpen(false)}
        property={property}
        prediction={pred}
        propertyAnalysis={propertyAnalysis}
        mlRent={computedValues.mlRent}
        agentRent={computedValues.agentRent}
        userRent={computedValues.highRent}
        discountedPrice={computedValues.discountedPrice}
        discountPct={checklistData?.discount_pct ? parseFloat(checklistData.discount_pct) : 0}
        computedValues={computedValues}
        checklistData={checklistData}
      />
    </div>
  );
}

// Static Google Map preview with a pin at the property address.
// The whole box links out to Google Maps (opens in a new tab).
const AddressMap = ({ property }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // Load the Maps JS SDK (same loader/key MapPage uses). Hook must run
  // unconditionally, so call it before any early return.
  const { isLoaded } = useLoadScript({ googleMapsApiKey: apiKey || "" });

  if (!property || !property.address || !apiKey) return null;

  const query = encodeURIComponent(
    [property.address, property.borough, "NY"].filter(Boolean).join(", ")
  );
  const gmapsLink = `https://www.google.com/maps/search/?api=1&query=${query}`;

  const hasCoords =
    typeof property.latitude === "number" &&
    typeof property.longitude === "number";

  const overlayLink = (
    <a
      href={gmapsLink}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Google Maps"
      style={{
        position: "absolute", bottom: "8px", right: "8px", zIndex: 1,
        background: "rgba(255,255,255,0.95)", color: "#1a73e8",
        fontSize: "12px", fontWeight: 600, padding: "4px 10px",
        borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        textDecoration: "none",
      }}
    >
      Open in Google Maps &#8599;
    </a>
  );

  // Interactive map (zoom/drag/pan) when we have coordinates + SDK loaded.
  if (hasCoords && isLoaded) {
    const center = { lat: property.latitude, lng: property.longitude };
    return (
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "260px" }}
          center={center}
          zoom={15}
          options={{
            gestureHandling: "greedy",   // single-finger drag + scroll-zoom
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
          }}
        >
          <Marker position={center} />
        </GoogleMap>
        {overlayLink}
      </div>
    );
  }

  // Fallback: static image (no coords yet, or SDK still loading).
  const staticMapUrl =
    `https://maps.googleapis.com/maps/api/staticmap?center=${query}` +
    `&zoom=15&size=600x260&scale=2&markers=color:red%7C${query}&key=${apiKey}`;
  return (
    <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
      <img
        src={staticMapUrl}
        alt={`Map of ${property.address}`}
        style={{ display: "block", width: "100%", height: "auto" }}
      />
      {overlayLink}
    </div>
  );
};

const PriceHistory = ({ listingId }) => {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (history.length > 0) { setExpanded(!expanded); return; }
    setExpanded(true);
    setLoading(true);
    try {
      const data = await authFetch(`/listings/${listingId}/history`);
      setHistory(data.history || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    }
    setLoading(false);
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
      <div
        onClick={load}
        style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <span style={{ fontWeight: 600, fontSize: "14px" }}>
          <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.2s", marginRight: "8px", fontSize: "10px" }}>&#9654;</span>
          Price History
        </span>
        <span style={{ fontSize: "12px", color: "#999" }}>{history.length > 0 ? `${history.length} entries` : "Click to load"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: "12px" }}>
          {loading ? (
            <div style={{ color: "#999", fontSize: "13px" }}>Loading...</div>
          ) : history.length === 0 ? (
            <div style={{ color: "#999", fontSize: "13px" }}>No price history available</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #eee" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#666", fontSize: "12px" }}>Date</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#666", fontSize: "12px" }}>Price</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#666", fontSize: "12px" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#666", fontSize: "12px" }}>Event</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "4px 8px" }}>{formatDate(h.changed_date)}</td>
                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{h.list_price ? `$${h.list_price.toLocaleString()}` : "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{h.listing_status || "-"}</td>
                    <td style={{ padding: "4px 8px", color: "#666" }}>{h.event_name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const PropertyDetails = ({ property, prediction, propertyAnalysis, checklistData, agentAnalysis, computedValues, onEdit }) => {
  const [showNeighborhoodTooltip, setShowNeighborhoodTooltip] = useState(false);

  // Use computedValues from parent (single source of truth)
  const originalPrice = computedValues?.originalPrice || property?.price || property?.asking_price || 0;
  const discountPct = computedValues?.discountPct || 0;
  const discountedPrice = computedValues?.discountedPrice || originalPrice;

  // Add financed other costs calculation for display
  const otherCostsItems = checklistData?.other_costs_items || [];
  const financedCosts = otherCostsItems
    .filter(item => item.payment_type === "financed" && item.cost)
    .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);

  // Calculate rental analysis using computedValues for consistency
  const analysis = calculateRentalAnalysis({
    property,
    prediction,
    propertyAnalysis,
    mlRent: computedValues?.mlRent || 0,
    agentRent: computedValues?.agentRent || 0,
    userRent: computedValues?.highRent || 0,
    discountedPrice,
    lowRent: computedValues?.lowRent || 0,
    highRent: computedValues?.highRent || 0,
    monthlyAssessment: computedValues?.monthlyAssessment || 0,
  });

  // Get max rent and corresponding metrics
  const capRate = analysis?.highIO?.capRate || 0;
  const irrYear7 = analysis?.highIO?.irrYear7 || 0;

  // Cap rate from a specific rent (mirrors the highIO scenario above) for the ML vs AI display.
  // aiCapRate updates whenever the AI rent (computedValues.agentRent) changes on an AI refresh.
  const capRateForRent = (rent) => {
    if (!rent) return null;
    const a = calculateRentalAnalysis({
      property, prediction, propertyAnalysis,
      mlRent: rent, agentRent: rent, userRent: rent, lowRent: rent, highRent: rent,
      discountedPrice, monthlyAssessment: computedValues?.monthlyAssessment || 0,
    });
    return a?.highIO?.capRate ?? null;
  };
  const mlCapRate = capRateForRent(computedValues?.mlRent);
  const aiCapRate = capRateForRent(computedValues?.agentRent);

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const infoRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #eee",
  };

  const linkStyle = {
    color: "#000",
    textDecoration: "none",
    fontWeight: "500",
    padding: "4px 8px",
    border: "1px solid #000",
    borderRadius: "4px",
    transition: "all 0.3s ease",
  };

  return (
    <div style={sectionStyle}>
      {/* Heading + address/unit/price removed — they live in the top bar above the photos
          (with the edit pencil + price diff). Two-column grid layout. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
        {/* Left Column */}
        <div>
          {(property.days_on_market != null || property.listed_date) && (
            <div style={infoRowStyle}>
              <strong>Days on Market:</strong>
              <span>
                {property.days_on_market != null
                  ? `${property.days_on_market} days`
                  : `${Math.floor((new Date() - new Date(property.listed_date)) / (1000 * 60 * 60 * 24))} days`}
                {property.off_market_days > 0 && (
                  <span style={{ fontSize: "11px", color: "#999", marginLeft: "6px" }}>
                    ({property.calendar_days}d calendar, {property.off_market_days}d off-market)
                  </span>
                )}
              </span>
            </div>
          )}
          {/* Listing Price row removed — raw price + the price-change diff now live in the top bar. */}
          {discountPct > 0 && (
            <div style={infoRowStyle}>
              <strong>Discount %:</strong>
              <span>{discountPct.toFixed(1)}%</span>
            </div>
          )}
          {financedCosts > 0 && (
            <div style={infoRowStyle}>
              <strong>Other Costs (Financed):</strong>
              <span style={{ color: "#dc3545", fontWeight: "600" }}>+${financedCosts.toLocaleString()}</span>
            </div>
          )}
          {(discountPct > 0 || financedCosts > 0) && (
            <div style={infoRowStyle}>
              <strong>Final Asking Price:</strong>
              <span style={{ color: "#28a745", fontWeight: "600", fontSize: "16px" }}>${discountedPrice.toLocaleString()}</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Size:</strong>
            <span>{property.sqft} sqft, {property.bedrooms}BR/{property.bathrooms}BA</span>
          </div>
          {property.price && property.sqft && (
            <div style={infoRowStyle}>
              <strong>Price per sqft:</strong>
              <span>${Math.round(property.price / property.sqft).toLocaleString()}</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Neighborhood:</strong>
            <span>
              {property.neighborhood}
              {(property.neighborhood_avg_cap_rate || property.neighborhood_weighted_avg_cap_rate) && (
                <span
                  style={{
                    marginLeft: "8px",
                    display: "inline-block",
                    position: "relative"
                  }}
                  onMouseEnter={() => setShowNeighborhoodTooltip(true)}
                  onMouseLeave={() => setShowNeighborhoodTooltip(false)}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textAlign: "center",
                      lineHeight: "16px",
                      cursor: "help"
                    }}
                  >
                    i
                  </span>
                  {showNeighborhoodTooltip && (
                    <div
                      style={{
                        position: "absolute",
                        top: "25px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#333",
                        color: "#fff",
                        padding: "12px 16px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        lineHeight: "1.6",
                        whiteSpace: "nowrap",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                        pointerEvents: "none"
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid #555", paddingBottom: "6px" }}>
                        Neighborhood Statistics
                      </div>
                      {property.neighborhood_weighted_avg_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Weighted Avg Cap Rate:</strong> {property.neighborhood_weighted_avg_cap_rate.toFixed(2)}%
                        </div>
                      )}
                      {property.neighborhood_avg_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Simple Avg Cap Rate:</strong> {property.neighborhood_avg_cap_rate.toFixed(2)}%
                        </div>
                      )}
                      {property.neighborhood_median_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Median Cap Rate:</strong> {property.neighborhood_median_cap_rate.toFixed(2)}%
                        </div>
                      )}
                      {property.neighborhood_avg_price && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Avg Asking Price:</strong> ${property.neighborhood_avg_price.toLocaleString()}
                        </div>
                      )}
                      {property.neighborhood_avg_predicted_rent && (
                        <div>
                          <strong>Avg Predicted Rent:</strong> ${property.neighborhood_avg_predicted_rent.toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </span>
              )}
            </span>
          </div>
          {/* Unit Features - Left Side */}
          {property.floor && (
            <div style={infoRowStyle}>
              <strong>Floor:</strong>
              <span>{property.floor}</span>
            </div>
          )}
          {property.floor_line && (
            <div style={infoRowStyle}>
              <strong>Floor Line:</strong>
              <span>{property.floor_line}</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>DW/WD:</strong>
            <span>
              {property.dishwasher === true ? 'Y' : property.dishwasher === false ? 'N' : '-'}
              {' / '}
              {property.washer_dryer === true ? 'Y' : property.washer_dryer === false ? 'N' : '-'}
            </span>
          </div>
        </div>

        {/* Right Column */}
        <div>
          <div style={infoRowStyle}>
            <strong>{property?.property_type?.toLowerCase() === 'coop' || property?.property_type?.toLowerCase() === 'condop' ? 'Monthly Maintenance Fee:' : 'Monthly HOA:'}</strong>
            <span>${property?.monthly_hoa?.toLocaleString() || 0}</span>
          </div>
          {property?.property_type?.toLowerCase() !== 'coop' && property?.property_type?.toLowerCase() !== 'condop' && (
            <div style={infoRowStyle}>
              <strong>Monthly Tax:</strong>
              <span>${property?.monthly_tax?.toLocaleString() || 0}</span>
            </div>
          )}
          {checklistData?.assessment_enabled && checklistData?.assessment_cost && (() => {
            const monthlyCost = parseInt(checklistData.assessment_cost) || 0;
            const isForever = checklistData.assessment_type === "forever";
            const hasEndDate = checklistData.assessment_type === "monthly" && checklistData.assessment_end_date;
            if (!monthlyCost) return null;
            if (isForever) {
              return (
                <div style={infoRowStyle}>
                  <strong>Assessment (ongoing):</strong>
                  <span style={{ color: "#c62828", fontWeight: "600" }}>${monthlyCost.toLocaleString()}/mo</span>
                </div>
              );
            }
            if (hasEndDate) {
              const remainingMonths = Math.max(0, Math.ceil((new Date(checklistData.assessment_end_date) - new Date()) / (1000 * 60 * 60 * 24 * 30.44)));
              const totalCost = monthlyCost * remainingMonths;
              const creditPct = checklistData.assessment_credit_pct ? parseFloat(checklistData.assessment_credit_pct) : 0;
              const effectiveTotal = Math.round(totalCost * (1 - creditPct / 100));
              return (
                <>
                  <div style={infoRowStyle}>
                    <strong>Assessment:</strong>
                    <span style={{ color: "#c62828", fontWeight: "600" }}>
                      ${monthlyCost.toLocaleString()}/mo × {remainingMonths}mo = ${totalCost.toLocaleString()}
                    </span>
                  </div>
                  {creditPct > 0 && (
                    <div style={infoRowStyle}>
                      <strong>Assessment (after {creditPct}% credit):</strong>
                      <span style={{ color: "#1565c0", fontWeight: "600" }}>${effectiveTotal.toLocaleString()}</span>
                    </div>
                  )}
                </>
              );
            }
            return null;
          })()}
          {(mlCapRate != null || aiCapRate != null) && (
            <div style={infoRowStyle}>
              <strong>Cap Rate ML/AI:</strong>
              <span style={{ color: "#28a745", fontWeight: "600" }}>
                {mlCapRate != null ? `${mlCapRate.toFixed(2)}%` : "—"}
                {" / "}
                {aiCapRate != null ? `${aiCapRate.toFixed(2)}%` : "—"}
              </span>
            </div>
          )}
          {capRate > 0 && (property?.neighborhood_avg_cap_rate || property?.neighborhood_weighted_avg_cap_rate) && (
            <div style={infoRowStyle}>
              <strong>RCR (simple/wtd):</strong>
              <span style={{ color: "#0066cc", fontWeight: "600" }}>
                {property?.neighborhood_avg_cap_rate ? (capRate / property.neighborhood_avg_cap_rate).toFixed(2) : "—"}
                {" / "}
                {property?.neighborhood_weighted_avg_cap_rate ? (capRate / property.neighborhood_weighted_avg_cap_rate).toFixed(2) : "—"}
              </span>
            </div>
          )}
          {irrYear7 > 0 && (
            <div style={infoRowStyle}>
              <strong>IRR (Year 7):</strong>
              <span style={{ color: "#28a745", fontWeight: "600" }}>{irrYear7.toFixed(2)}%</span>
            </div>
          )}
          {/* Web Listing row removed — the link is now an icon next to the building type in the top bar. */}
          {/* Unit Features - Right Side. Dishwasher moved into the combined DW/WD row (left column). */}
          {property.renovation && (
            <div style={infoRowStyle}>
              <strong>Renovation:</strong>
              <span>{property.renovation}</span>
            </div>
          )}
          {/* Premium Views as Y/N. ai_views is dominantly a 0/1 flag ("1"=premium view, "0"=none),
              plus some descriptive strings ("City", "Bright Sun") which also count as Y. */}
          <div style={infoRowStyle}>
            <strong>Premium Views:</strong>
            <span>{property.views && String(property.views).trim() !== "0" ? "Y" : "N"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PredictionResults = ({ prediction, property, agentAnalysis, handleClosingCostsSave, handleMortgageSave, propertyAnalysis, checklistData, computedValues, imageModalOpen, selectedImage, closeImageModal, navigateImage, currentImageIndex, allImages, openIRRModal }) => {
  // State for Mortgage Config Modal
  const [isMortgageModalOpen, setIsMortgageModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [mortgageConfig, setMortgageConfig] = useState(null);

  // State for Closing Costs Modal
  const [isClosingCostsModalOpen, setIsClosingCostsModalOpen] = useState(false);

  // Use values from computedValues (single source of truth from parent)
  const mlRent = computedValues.mlRent;
  const agentRent = computedValues.agentRent;
  const discountPct = computedValues.discountPct;
  const askingPrice = computedValues.discountedPrice;
  const totalCarryingCosts = computedValues.monthlyCarryingCosts;

  // Calculate for ML prediction using centralized utility
  const mlCashFlow = mlRent - totalCarryingCosts;
  const mlNetCapRate = calculateCapRate(mlRent, totalCarryingCosts, askingPrice).toFixed(2);

  // Calculate for Agent prediction using centralized utility
  const agentCashFlow = agentRent - totalCarryingCosts;
  const agentNetCapRate = calculateCapRate(agentRent, totalCarryingCosts, askingPrice).toFixed(2);

  // Calculate for Tenant rent
  const tenantRent = checklistData?.tenant_current_rent ? parseFloat(checklistData.tenant_current_rent) : 0;
  const tenantCashFlow = tenantRent - totalCarryingCosts;
  const tenantNetCapRate = tenantRent > 0 ? calculateCapRate(tenantRent, totalCarryingCosts, askingPrice).toFixed(2) : 0;

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const comparisonColumnStyle = {
    flex: 1,
    padding: "10px",
    borderRadius: "6px",
  };

  const mlColumnStyle = {
    ...comparisonColumnStyle,
    backgroundColor: "#f8f9fa",
    border: "2px solid #6c757d",
  };

  const agentColumnStyle = {
    ...comparisonColumnStyle,
    backgroundColor: "#fff3cd",
    border: "2px solid #ffc107",
  };

  const tenantColumnStyle = {
    ...comparisonColumnStyle,
    backgroundColor: "#ffebee",
    border: "2px solid #c62828",
  };

  const columnHeaderStyle = {
    fontSize: "13px",
    fontWeight: "700",
    marginBottom: "8px",
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const valueRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: "12px",
    borderBottom: "1px solid #ddd",
  };

  const bigNumberStyle = {
    fontSize: "18px",
    fontWeight: "700",
    textAlign: "center",
    margin: "6px 0",
  };

  return (
    <div style={sectionStyle}>
      <h2 style={headingStyle}>Rent Prediction</h2>

      {/* Three Buttons: IRR Assumptions, Closing Costs, IRR Report */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
        <button
          onClick={() => setIsMortgageModalOpen(true)}
          style={{
            flex: 1,
            padding: "10px 16px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background-color 0.3s ease",
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = "#5a6268"}
          onMouseLeave={(e) => e.target.style.backgroundColor = "#6c757d"}
        >
          <span style={{ fontSize: "14px" }}>🏦</span>
          IRR Assumptions
        </button>
        <button
          onClick={() => setIsClosingCostsModalOpen(true)}
          style={{
            flex: 1,
            padding: "10px 16px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background-color 0.3s ease",
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = "#5a6268"}
          onMouseLeave={(e) => e.target.style.backgroundColor = "#6c757d"}
        >
          <span style={{ fontSize: "14px" }}>📋</span>
          Closing costs
        </button>
        <button
          onClick={openIRRModal}
          style={{
            flex: 1,
            padding: "10px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background-color 0.3s ease",
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
          onMouseLeave={(e) => e.target.style.backgroundColor = "#007bff"}
        >
          <span style={{ fontSize: "14px" }}>📈</span>
          IRR Report
        </button>
      </div>

      {/* Mortgage Config Modal */}
      <MortgageConfigModal
        isOpen={isMortgageModalOpen}
        onClose={() => setIsMortgageModalOpen(false)}
        propertyPrice={askingPrice}
        propertyId={property?.id}
        onSave={(config) => {
          setMortgageConfig(config);
          handleMortgageSave(config);
        }}
      />

      {/* Closing Costs Modal */}
      <ClosingCostsModal
        isOpen={isClosingCostsModalOpen}
        onClose={() => setIsClosingCostsModalOpen(false)}
        property={property}
        propertyId={property?.id}
        askingPrice={askingPrice}
        hasFinancing={mortgageConfig !== null}
        onSave={handleClosingCostsSave}
        checklistData={checklistData}
        downPaymentPercent={propertyAnalysis?.down_payment_percent}
      />

      {/* Image Modal */}
      {imageModalOpen && selectedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={closeImageModal}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "90%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeImageModal}
              style={{
                position: "absolute",
                top: "-40px",
                right: "0",
                background: "white",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "700",
                color: "#333",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              ×
            </button>

            {/* Label and Counter */}
            <div
              style={{
                position: "absolute",
                top: "-40px",
                left: "0",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {selectedImage.label && (
                <div
                  style={{
                    backgroundColor: selectedImage.label === "Floor Plan" ? "#4CAF50" : "#333",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {selectedImage.label}
                </div>
              )}
              {allImages.length > 1 && (
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.9)",
                    color: "#333",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              )}
            </div>

            {/* Previous Button */}
            {allImages.length > 1 && currentImageIndex > 0 && (
              <button
                onClick={() => navigateImage(-1)}
                style={{
                  position: "absolute",
                  left: "-60px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "48px",
                  height: "48px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  color: "#333",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#f0f0f0"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
              >
                ←
              </button>
            )}

            {/* Next Button */}
            {allImages.length > 1 && currentImageIndex < allImages.length - 1 && (
              <button
                onClick={() => navigateImage(1)}
                style={{
                  position: "absolute",
                  right: "-60px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "48px",
                  height: "48px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  color: "#333",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#f0f0f0"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
              >
                →
              </button>
            )}

            {/* Image or Video/Tour Embed */}
            {(selectedImage.type === "video" || selectedImage.type === "tour") ? (
              <iframe
                title={selectedImage.label || "Media"}
                src={selectedImage.url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; xr-spatial-tracking"
                allowFullScreen
                style={{
                  width: "min(90vw, 1200px)",
                  height: "min(70vh, 675px)",
                  aspectRatio: "16 / 9",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                  backgroundColor: "#000",
                }}
              />
            ) : (
              <img
                src={selectedImage.url}
                alt={selectedImage.label || "Property image"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(90vh - 60px)",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                }}
              />
            )}

            {/* Download link */}
            <a
              href={selectedImage.originalUrl || selectedImage.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginTop: "16px",
                color: "white",
                textDecoration: "none",
                fontSize: "14px",
                padding: "8px 16px",
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "4px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255,255,255,0.3)"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "rgba(255,255,255,0.2)"}
            >
              {selectedImage.type === "video"
                ? "Watch on YouTube ↗"
                : selectedImage.type === "tour"
                  ? "Open tour in new tab ↗"
                  : "Open in new tab ↗"}
            </a>
          </div>
        </div>
      )}

      {/* ML vs Agent Comparison */}
      <div style={{display: "flex", gap: "16px"}}>
        {/* ML Prediction Column */}
        <div style={mlColumnStyle}>
          <div style={{...columnHeaderStyle, color: "#6c757d"}}>ML Prediction</div>
          <div style={bigNumberStyle}>${mlRent?.toLocaleString()}</div>
          <div style={valueRowStyle}>
            <span>Cap Rate (Net):</span>
            <strong style={{color: "#28a745"}}>{mlNetCapRate}%</strong>
          </div>
          <div style={valueRowStyle}>
            <span>Cash Flow:</span>
            <strong>${mlCashFlow?.toLocaleString()}</strong>
          </div>
        </div>

        {/* Agent Prediction Column */}
        {agentRent > 0 && (
          <div style={agentColumnStyle}>
            <div style={{...columnHeaderStyle, color: "#856404"}}>AI Agent</div>
            <div style={bigNumberStyle}>${agentRent?.toLocaleString()}</div>
            <div style={valueRowStyle}>
              <span>Cap Rate (Net):</span>
              <strong style={{color: "#28a745"}}>{agentNetCapRate}%</strong>
            </div>
            <div style={valueRowStyle}>
              <span>Cash Flow:</span>
              <strong>${agentCashFlow?.toLocaleString()}</strong>
            </div>
          </div>
        )}

        {/* Current Tenant Column */}
        {tenantRent > 0 && (
          <div style={tenantColumnStyle}>
            <div style={{...columnHeaderStyle, color: "#c62828"}}>Current Tenant</div>
            <div style={{...bigNumberStyle, color: "#c62828"}}>${tenantRent?.toLocaleString()}</div>
            <div style={valueRowStyle}>
              <span>Cap Rate (Net):</span>
              <strong style={{color: "#28a745"}}>{tenantNetCapRate}%</strong>
            </div>
            <div style={valueRowStyle}>
              <span>Cash Flow:</span>
              <strong>${tenantCashFlow?.toLocaleString()}</strong>
            </div>
          </div>
        )}
      </div>

      {/* AI vs ML Divergence Warning */}
      {mlRent > 0 && agentRent > 0 && (() => {
        const divergence = Math.round(Math.abs(agentRent - mlRent) / mlRent * 100);
        if (divergence < 15) return null;
        const isHigh = divergence >= 25;
        const higher = agentRent > mlRent ? "AI" : "ML";
        return (
          <div style={{
            padding: "12px 16px",
            borderRadius: "6px",
            borderLeft: `4px solid ${isHigh ? "#c62828" : "#f57c00"}`,
            background: isHigh ? "#ffebee" : "#fff3e0",
            fontSize: "13px",
            color: "#333",
            marginTop: "8px",
          }}>
            <strong style={{ color: isHigh ? "#c62828" : "#e65100" }}>
              {isHigh ? "⚠ Significant divergence" : "⚠ Review recommended"}
            </strong>
            {" — "}AI and ML differ by <strong>{divergence}%</strong> ({higher} is higher).
            {isHigh
              ? " Check data quality, comps, and bedroom count."
              : " Review comps for potential issues."}
          </div>
        );
      })()}
    </div>
  );
};

const SameBuildingAnalysis = ({ analysis }) => {
  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    background: "linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #28a745",
    paddingBottom: "8px",
  };

  const infoRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #c3e6cb",
  };

  const unitStyle = {
    backgroundColor: "rgba(255,255,255,0.7)",
    padding: "12px",
    borderRadius: "4px",
    margin: "8px 0",
    border: "1px solid #c3e6cb",
  };

  return (
    <div style={sectionStyle}>
      <h2 style={headingStyle}>Same Building Analysis</h2>
      <div style={infoRowStyle}>
        <strong>Units Found:</strong>
        <span>{analysis.units_found}</span>
      </div>
      <div style={infoRowStyle}>
        <strong>Anchor Rent:</strong>
        <span>${analysis.anchor_rent?.toLocaleString()}</span>
      </div>
      <div style={infoRowStyle}>
        <strong>Size Adjustment:</strong>
        <span>{analysis.size_adjustment}x</span>
      </div>
      
      {analysis.units && (
        <div style={{ marginTop: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>
            Same Building Units:
          </h3>
          {analysis.units.map((unit, index) => (
            <div key={index} style={unitStyle}>
              <strong>{unit.address}</strong>: ${unit.rent?.toLocaleString()} 
              ({unit.sqft} sqft, {unit.bedrooms}BR/{unit.bathrooms}BA)
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OutOfPocketSection = ({ property, prediction, propertyAnalysis, checklistData, onUpdate }) => {
  // Calculate adjusted asking price (including financed other costs)
  const originalPrice = property?.price || property?.asking_price || 0;
  const discountPct = checklistData?.discount_pct ? parseFloat(checklistData.discount_pct) : 0;
  let askingPrice = originalPrice * (1 - discountPct / 100);

  // Add financed other costs to the asking price
  const otherCostsItems = checklistData?.other_costs_items || [];
  const financedCosts = otherCostsItems
    .filter(item => item.payment_type === "financed" && item.cost)
    .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
  askingPrice += financedCosts;

  const downPaymentPercent = propertyAnalysis?.down_payment_percent || 25;
  // Use saved down payment amount if available, otherwise calculate from percentage
  const downPayment = propertyAnalysis?.down_payment_amount
    ? Math.round(propertyAnalysis.down_payment_amount)
    : Math.round(askingPrice * (downPaymentPercent / 100));

  // Get carrying costs from property (single source of truth)
  const monthlyTax = property?.monthly_tax || 0;
  const monthlyHOA = property?.monthly_hoa || 0;

  const renovationCost = propertyAnalysis?.renovation_cost || 0;
  const timeToRenovate = propertyAnalysis?.time_to_renovate || 0;
  const netClosingCosts = propertyAnalysis?.net_closing_costs || 0;

  // Calculate monthly mortgage payment
  // Use saved mortgage data if available, otherwise use defaults
  const mortgageAmount = askingPrice - downPayment;

  // Check if we have saved mortgage payment from modal, otherwise calculate default
  let monthlyMortgagePayment = 0;
  if (propertyAnalysis?.monthly_payment) {
    // Use saved monthly payment from mortgage modal
    monthlyMortgagePayment = Math.round(propertyAnalysis.monthly_payment);
    console.log('💰 Using saved monthly mortgage payment:', monthlyMortgagePayment);
  } else {
    // Calculate default: 7% rate, 30-year term, P&I
    const annualInterestRate = 7;
    const monthlyInterestRate = annualInterestRate / 100 / 12;
    const numberOfPayments = 30 * 12;

    monthlyMortgagePayment = mortgageAmount > 0
      ? Math.round(mortgageAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1))
      : 0;
    console.log('💰 Using default calculated monthly mortgage payment:', monthlyMortgagePayment);
  }

  // Formula: Down Payment + Net Closing Costs + Renovation Cost + (Time to Renovate × (Monthly Tax + Monthly HOA + Monthly Mortgage))
  // Only add renovation-related costs if BOTH renovation cost AND time to renovate are > 0
  const renovationRelatedCosts = (renovationCost > 0 && timeToRenovate > 0)
    ? Math.round(renovationCost + (timeToRenovate * (monthlyTax + monthlyHOA + monthlyMortgagePayment)))
    : 0;

  const totalOutOfPocket = Math.round(downPayment + netClosingCosts + renovationRelatedCosts);

  // Handle input changes with debounced save
  const handleRenovationChange = (value) => {
    const updatedAnalysis = {
      ...propertyAnalysis,
      renovation_cost: parseFloat(value) || 0
    };
    onUpdate(updatedAnalysis);
  };

  const handleTimeToRenovateChange = (value) => {
    const updatedAnalysis = {
      ...propertyAnalysis,
      time_to_renovate: parseFloat(value) || 0
    };
    onUpdate(updatedAnalysis);
  };

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  };

  const inputStyle = {
    padding: "10px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    textAlign: "right",
  };

  return (
    <div style={sectionStyle}>
      <h2 style={headingStyle}>Out of Pocket</h2>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", marginBottom: "20px" }}>
        {/* Down Payment (calculated) */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Down Payment ({Number(downPaymentPercent).toFixed(2)}%)</label>
          <input
            type="text"
            style={{ ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            value={`$${downPayment.toLocaleString()}`}
            disabled
          />
        </div>

        {/* Renovation */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Renovation</label>
          <CurrencyInput
            style={inputStyle}
            value={renovationCost}
            onChange={handleRenovationChange}
          />
        </div>

        {/* Net Closing Costs (greyed out) */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Net Closing Costs</label>
          <input
            type="text"
            style={{ ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            value={`$${Math.round(netClosingCosts).toLocaleString()}`}
            disabled
          />
        </div>

        {/* Time to Renovate */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Time to Renovate (months)</label>
          <input
            type="number"
            style={inputStyle}
            value={timeToRenovate}
            onChange={(e) => handleTimeToRenovateChange(e.target.value)}
            min="0"
            step="1"
          />
        </div>
      </div>

      {/* Total Out of Pocket */}
      <div style={{ padding: "20px", backgroundColor: "#fff3cd", borderRadius: "8px", border: "2px solid #ffc107" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "18px", fontWeight: "700", color: "#333" }}>TOTAL OUT OF POCKET</span>
          <span style={{ fontSize: "28px", fontWeight: "700", color: "#1e3a8a" }}>
            ${totalOutOfPocket.toLocaleString()}
          </span>
        </div>
        <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
          Down Payment + Net Closing Costs + Renovation + (Time to Renovate × (Monthly Tax + Monthly HOA + Monthly Mortgage))
        </div>
      </div>
    </div>
  );
};

const TIER_COLORS = {
  1: "#e8f5e9",
  2: "#e3f2fd",
  3: "#fff8e1",
  4: "#fce4ec",
};

const TIER_LABELS = {
  1: "Same Bldg (0-12mo)",
  2: "Same Bldg (12-24mo)",
  3: "Zone+Tier (0-12mo)",
  4: "Zone+Tier (12-24mo)",
};

const AIPredictionAnalysis = ({ aiPrediction, comps, tenantCurrentRent, propertyId, predictionId, listingId, onRerun, minCompScore, setMinCompScore, minCompRent, setMinCompRent, compBedroomsOverride, setCompBedroomsOverride, compMinSqft, setCompMinSqft, compMaxSqft, setCompMaxSqft, targetBedrooms, rerunMsg }) => {
  // Map comp rank -> comp (for linking each per-comp adjustment to its unit/listing)
  const compByRank = useMemo(() => {
    const m = {};
    (comps || []).forEach((c) => { if (c?.rank != null) m[c.rank] = c; });
    return m;
  }, [comps]);
  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: 0,
    borderBottom: "none",
    paddingBottom: 0,
  };

  const confidenceColor = aiPrediction.confidence === "high" ? "#2e7d32"
    : aiPrediction.confidence === "medium" ? "#f57c00" : "#d32f2f";

  // Currently-rented units: show the in-place/current rent (which drives cap rate — kept
  // conservative) alongside the AI's independent MARKET estimate, with an over/under-market
  // badge. Current = manually-entered tenant_current_rent, else the proven same-unit rent the
  // backend surfaced. They may be equal (unit is renting at market).
  const manualCurrent = tenantCurrentRent ? parseFloat(tenantCurrentRent) : null;
  const currentRent = (manualCurrent && manualCurrent > 0) ? manualCurrent : (aiPrediction.current_rent || null);
  const marketRent = aiPrediction.market_rent || aiPrediction.predicted_rent || null;
  const showDual = !!(currentRent && marketRent);
  let rentGapPct = null, rentDir = null;
  if (showDual && marketRent > 0) {
    rentGapPct = Math.round(((currentRent - marketRent) / marketRent) * 1000) / 10;
    rentDir = Math.abs(rentGapPct) <= 8 ? "in_line" : (rentGapPct > 0 ? "over" : "under");
  }
  const gapColor = rentDir === "in_line" ? "#2e7d32" : (rentDir === "over" ? "#d32f2f" : "#f57c00");
  const gapLabel = rentDir === "in_line" ? "at market"
    : rentDir === "over" ? `${Math.abs(rentGapPct)}% over market`
    : `${Math.abs(rentGapPct)}% under market`;

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={headingStyle}>AI Rent Prediction</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onRerun && (
            <>
              <select
                value={minCompScore}
                onChange={(e) => setMinCompScore(parseInt(e.target.value))}
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  color: "#333",
                }}
                title="Minimum comp similarity score"
              >
                <option value={70}>Super strict (70+)</option>
                      <option value={50}>Strict (50+)</option>
                <option value={30}>Relaxed (30+)</option>
                <option value={0}>All comps</option>
              </select>
              <input
                type="number"
                value={minCompRent}
                onChange={(e) => setMinCompRent(e.target.value)}
                placeholder="Min rent $"
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  color: "#333",
                  width: 90,
                }}
                title="Minimum comp rent (only include comps at or above this rent)"
              />
              <input
                type="number"
                value={compBedroomsOverride}
                onChange={(e) => setCompBedroomsOverride(e.target.value)}
                placeholder={targetBedrooms != null ? `${targetBedrooms} BR` : "Beds"}
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  color: "#333",
                  width: 60,
                }}
                title="Override bedrooms for comp matching"
                step="1"
                min="0"
                max="5"
              />
              <input
                type="number"
                value={compMinSqft}
                onChange={(e) => setCompMinSqft(e.target.value)}
                placeholder="Min sqft"
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  color: "#333",
                  width: 80,
                }}
                title="Minimum comp sqft"
              />
              <input
                type="number"
                value={compMaxSqft}
                onChange={(e) => setCompMaxSqft(e.target.value)}
                placeholder="Max sqft"
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  color: "#333",
                  width: 80,
                }}
                title="Maximum comp sqft"
              />
              <button
                onClick={onRerun}
                title="Re-run comps against today's market and regenerate the AI analysis. If the comp set is unchanged, nothing is overwritten and the AI re-run is skipped."
                style={{
                  padding: "6px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: "1px solid #1976d2",
                  background: "#fff",
                  color: "#1976d2",
                  cursor: "pointer",
                }}
              >
                Re-run
              </button>
            </>
          )}
          {propertyId && predictionId && (
            <ChatComponent
              predictionId={predictionId}
              propertyId={propertyId}
            />
          )}
        </div>
      </div>
      {rerunMsg && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            marginBottom: 12,
            textAlign: "right",
            color: rerunMsg.startsWith("No change") ? "#666" : "#2e7d32",
          }}
        >
          {rerunMsg}
        </div>
      )}
      <div style={{ borderBottom: '2px solid #2196f3', marginBottom: '16px' }}></div>

      {/* Main prediction values */}
      <div style={{ display: "flex", gap: 32, alignItems: "baseline", marginBottom: 12, flexWrap: "wrap" }}>
        {showDual ? (
          <>
            <div>
              <span style={{ fontSize: 13, color: "#666" }}>Current Rent</span>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#1976d2" }}>
                ${currentRent?.toLocaleString()}/mo
              </div>
              {rentDir && (
                <div style={{ fontSize: 12, fontWeight: 600, color: gapColor }}>{gapLabel}</div>
              )}
              <span style={{ fontSize: 11, color: "#999" }}>in-place · used for cap rate</span>
            </div>
            <div>
              <span style={{ fontSize: 13, color: "#666" }}>Predicted (Market)</span>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#455a64" }}>
                ${marketRent?.toLocaleString()}/mo
              </div>
            </div>
          </>
        ) : (
          <div>
            <span style={{ fontSize: 13, color: "#666" }}>Predicted Rent</span>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1976d2" }}>
              ${aiPrediction.predicted_rent?.toLocaleString()}/mo
            </div>
          </div>
        )}
        <div>
          <span style={{ fontSize: 13, color: "#666" }}>Range</span>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            ${aiPrediction.rent_low?.toLocaleString()} - ${aiPrediction.rent_high?.toLocaleString()}
          </div>
        </div>
        <div>
          <span style={{ fontSize: 13, color: "#666" }}>Confidence</span>
          <div style={{ fontSize: 16, fontWeight: 600, color: confidenceColor, textTransform: "uppercase" }}>
            {aiPrediction.confidence}
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#999" }}>
          {aiPrediction.total_comps} comps ({aiPrediction.same_building_comps} same bldg) | {aiPrediction.usage?.total_tokens} tokens
        </div>
      </div>

      {/* Reasoning */}
      {aiPrediction.reasoning && (
        <div style={{ padding: "12px", backgroundColor: "rgba(255,255,255,0.7)", borderRadius: "4px", margin: "12px 0", fontSize: "14px", lineHeight: 1.5 }}>
          <strong>Reasoning:</strong> {aiPrediction.reasoning}
        </div>
      )}

      {/* Key factors */}
      {aiPrediction.key_factors?.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>Key factors:</span>
          {aiPrediction.key_factors.map((f, i) => (
            <span key={i} style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 4,
              background: "#e3f2fd", color: "#1565c0",
            }}>
              {f}
            </span>
          ))}
        </div>
      )}

      {/* Per-comp adjustments */}
      {aiPrediction.comp_analysis?.length > 0 && (
        <details style={{ fontSize: 12, marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, color: "#555" }}>
            Per-comp adjustments ({aiPrediction.comp_analysis.length})
          </summary>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.5)", textAlign: "left" }}>
                <th style={{ padding: "4px 8px" }}>#</th>
                <th style={{ padding: "4px 8px" }}>Unit</th>
                <th style={{ padding: "4px 8px" }}>Actual Rent</th>
                <th style={{ padding: "4px 8px" }}>Adjusted</th>
                <th style={{ padding: "4px 8px" }}>Adjustments</th>
              </tr>
            </thead>
            <tbody>
              {aiPrediction.comp_analysis.map((ca) => {
                const comp = compByRank[ca.comp_rank];
                return (
                <tr key={ca.comp_rank} style={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                  <td style={{ padding: "4px 8px" }}>{ca.comp_rank}</td>
                  <td style={{ padding: "4px 8px" }}>
                    {comp?.address ? (
                      comp.listing_url ? (
                        <a href={comp.listing_url} target="_blank" rel="noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
                          {comp.address.trim()}
                        </a>
                      ) : comp.address.trim()
                    ) : "-"}
                  </td>
                  <td style={{ padding: "4px 8px" }}>${ca.rent?.toLocaleString()}</td>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>${ca.adjusted_rent?.toLocaleString()}</td>
                  <td style={{ padding: "4px 8px", color: "#555" }}>{ca.adjustments}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}

    </div>
  );
};

const BuildingHistoryContent = ({ history, thStyle, tdStyle, formatWD, onEditComp }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const rentals = history.filter(h => h.sale_or_rental === 'R');
  const sales = history.filter(h => h.sale_or_rental === 'S');

  const groupByBed = (items) => {
    const grouped = {};
    items.forEach(h => {
      const key = h.bedrooms === 0 ? "Studio" : h.bedrooms != null ? `${Math.floor(h.bedrooms)}BR` : "Unknown";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(h);
    });
    const bedOrder = ["Studio", "1BR", "2BR", "3BR", "4BR", "5BR", "Unknown"];
    return Object.keys(grouped).sort((a, b) => bedOrder.indexOf(a) - bedOrder.indexOf(b)).map(k => [k, grouped[k]]);
  };

  const renderTable = (items, isSales) => (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr>
          <th style={thStyle}>Unit</th>
          <th style={thStyle}>{isSales ? "Price" : "Rent"}</th>
          {isSales && <th style={thStyle}>Sold</th>}
          <th style={thStyle}>Sqft</th>
          <th style={thStyle}>Floor</th>
          <th style={thStyle}>Status</th>
          <th style={thStyle}>Date</th>
          <th style={thStyle}>DOM</th>
        </tr>
      </thead>
      <tbody>
        {items.map(h => (
          <tr key={h.listing_id} style={{ borderBottom: "1px solid #eee" }}>
            <td style={tdStyle}>
              {h.listing_url ? (
                <a href={h.listing_url} target="_blank" rel="noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
                  {h.unit_number || "-"}
                </a>
              ) : (h.unit_number || "-")}
              {onEditComp && (
                <span
                  title="Edit listing"
                  onClick={(e) => { e.stopPropagation(); onEditComp(h.listing_id); }}
                  style={{ fontSize: 13, marginLeft: 6, cursor: "pointer", opacity: 0.5 }}
                >&#9998;</span>
              )}
            </td>
            <td style={{ ...tdStyle, fontWeight: 600 }}>${h.price?.toLocaleString()}</td>
            {isSales && <td style={{ ...tdStyle, fontWeight: 600 }}>{h.closing_price ? `$${h.closing_price.toLocaleString()}` : "-"}</td>}
            <td style={tdStyle}>{h.sqft ? `${h.sqft.toLocaleString()}${h.sqft_is_estimated ? '*' : ''}` : "-"}</td>
            <td style={tdStyle}>{h.floor || "-"}</td>
            <td style={tdStyle}>
              <span style={{
                fontSize: 11, padding: "1px 6px", borderRadius: 3,
                background: (h.status === "RENTED" || h.status === "RENTED THRU US" || h.status === "SOLD" || h.status === "CLOSED" || h.status === "CLOSED THRU US") ? "#c8e6c9"
                  : h.status === "ACTIVE" ? "#fff9c4" : "#ffcdd2",
                color: (h.status === "ACTIVE" || h.status === "RENTED" || h.status === "RENTED THRU US" || h.status === "SOLD" || h.status === "CLOSED" || h.status === "CLOSED THRU US") ? "#333" : "#c62828",
              }}>
                {h.status === "RENTED THRU US" ? "Rented" : h.status === "CLOSED THRU US" ? "Sold" : h.status}
              </span>
            </td>
            <td style={{ ...tdStyle, fontSize: 11, whiteSpace: "nowrap" }}>
              {(h.closing_date || h.listed_at)?.slice(0, 10) || "-"}
            </td>
            <td style={{ ...tdStyle, fontSize: 11 }}>{h.active_dom != null ? h.active_dom : "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderSection = (label, items, color, bgColor, isSales) => {
    if (items.length === 0) return null;
    const groups = groupByBed(items);
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color, marginBottom: 12, borderBottom: `2px solid ${color}`, paddingBottom: 4 }}>
          {label} ({items.length})
        </div>
        {groups.map(([bedType, bedItems]) => {
          const key = `${isSales ? 's' : 'r'}-${bedType}`;
          const isExpanded = expanded[key];
          return (
            <div key={key} style={{ marginBottom: 8 }}>
              <div
                onClick={() => toggle(key)}
                style={{ fontWeight: 600, fontSize: 13, color: "#333", padding: "6px 8px", background: bgColor, borderRadius: 4, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span>{bedType} ({bedItems.length})</span>
                <span style={{ fontSize: 11, color: "#666" }}>{isExpanded ? "collapse" : "expand"}</span>
              </div>
              {isExpanded && <div style={{ marginTop: 4 }}>{renderTable(bedItems, isSales)}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {renderSection("Rentals", rentals, "#1565c0", "#e3f2fd", false)}
      {renderSection("Sales", sales, "#2e7d32", "#e8f5e9", true)}
    </>
  );
};

const CompScoringSection = ({ data, listingId, onEditComp, onExcludeComp }) => {
  const [activeTab, setActiveTab] = useState("comps"); // "comps" or "history"
  const [buildingHistory, setBuildingHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadBuildingHistory = async () => {
    if (buildingHistory) return; // already loaded
    setHistoryLoading(true);
    try {
      const res = await authFetch(`/comp-scoring/${listingId}/building-history`);
      setBuildingHistory(res);
    } catch (err) {
      console.error("Failed to load building history:", err);
    }
    setHistoryLoading(false);
  };

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "6px 4px",
    borderBottom: "2px solid #e0e0e0",
    fontWeight: "600",
    color: "#333",
    backgroundColor: "#f8f9fa",
    fontSize: "11px",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "4px",
    borderBottom: "1px solid #e0e0e0",
    fontSize: "12px",
  };

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const rawComps = data.comps || [];
  const target = data.target;
  const sf = target?.scoring_fields || {};

  const formatWD = (v) => v === "in_unit" ? "Unit" : v === "in_building" ? "Bldg" : "-";

  const getSortValue = (comp, field) => {
    switch (field) {
      case "rank": return comp.rank;
      case "rent": return comp.rent || 0;
      case "sqft": return comp.sqft || 0;
      case "reno": return comp.scoring_fields?.renovation || 0;
      case "floor": return comp.scoring_fields?.floor || 0;
      case "dw": return comp.scoring_fields?.dishwasher ? 1 : 0;
      case "wd": return comp.scoring_fields?.washer_dryer === "in_unit" ? 2 : comp.scoring_fields?.washer_dryer === "in_building" ? 1 : 0;
      case "out": return comp.scoring_fields?.outdoor_space ? 1 : 0;
      case "score": return comp.similarity_score || 0;
      case "dist": return comp.is_same_building ? -1 : (comp.distance_miles || 999);
      case "date": return comp.closing_date || comp.listed_at || "";
      case "dom": return comp.active_dom || 0;
      default: return 0;
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const comps = sortField
    ? [...rawComps].sort((a, b) => {
        const av = getSortValue(a, sortField);
        const bv = getSortValue(b, sortField);
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      })
    : rawComps;

  // Suspected-duplicate comps (same unit + same building, dates within 90 days \u2014
  // often the same rental entered twice with a transposed unit like "3J"/"J3").
  // Soft warning only; nothing is changed automatically.
  const duplicateFlags = data.duplicate_flags || [];
  const duplicateIds = new Set(duplicateFlags.flatMap(f => f.listing_ids || []));

  const sortIcon = (field) => sortField === field ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : "";

  const sortableThStyle = { ...thStyle, cursor: "pointer", userSelect: "none" };

  // Direction of a comp attribute vs the target (>0 = comp is "better" on that attr).
  const wdLevel = (v) => (v === "in_unit" ? 2 : v === "in_building" ? 1 : 0);
  const attrDelta = (attr, comp) => {
    const cf = comp.scoring_fields || {};
    switch (attr) {
      case "sqft": return (comp.sqft || 0) - (target?.sqft || sf.sqft || 0);
      case "renovation": return (cf.renovation || 0) - (sf.renovation || 0);
      case "floor": return (cf.floor || 0) - (sf.floor || 0);
      case "dishwasher": return (cf.dishwasher ? 1 : 0) - (sf.dishwasher ? 1 : 0);
      case "washer_dryer": return wdLevel(cf.washer_dryer) - wdLevel(sf.washer_dryer);
      case "outdoor_space": return (cf.outdoor_space ? 1 : 0) - (sf.outdoor_space ? 1 : 0);
      default: return 0;
    }
  };
  // Color a flagged-difference cell by its EFFECT ON THE TARGET RENT, not magnitude:
  // comp BETTER than target (delta>0) -> RED (this comp drags the predicted rent DOWN);
  // comp WORSE (delta<0) -> GREEN (pushes it UP). One comp can be red on some attrs, green on others.
  const featureCell = (attr, comp) => {
    const diff = comp.differences?.find((d) => d.attribute === attr);
    if (!diff) return tdStyle;
    const d = attrDelta(attr, comp);
    const bg = d > 0 ? "#ffcdd2" : d < 0 ? "#c8e6c9" : "#fff9c4";
    return { ...tdStyle, background: bg, fontWeight: 600 };
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ ...headingStyle, marginBottom: 0 }}>Comparable Rentals</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13 }}>
          <span>
            <strong>Score:</strong>{" "}
            <span style={{ color: data.cumulative_score >= 700 ? "#2e7d32" : "#d32f2f", fontWeight: 700 }}>
              {data.cumulative_score}
            </span>
          </span>
          <span><strong>{data.total_comps}</strong> comps</span>
          <span><strong>{data.same_building_comps}</strong> same bldg</span>
        </div>
      </div>

      {/* Color legend for the feature cells */}
      <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#555", marginBottom: 12, alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#ffcdd2", border: "1px solid #e0a0a0", display: "inline-block" }} />
          comp better than target → pulls predicted rent down
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#c8e6c9", border: "1px solid #a0c8a0", display: "inline-block" }} />
          comp worse → pushes it up
        </span>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "2px solid #e0e0e0" }}>
        <button
          onClick={() => setActiveTab("comps")}
          style={{
            padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
            border: "none", borderBottom: activeTab === "comps" ? "3px solid #1976d2" : "3px solid transparent",
            background: "none", color: activeTab === "comps" ? "#1976d2" : "#666",
          }}
        >Scored Comps</button>
        <button
          onClick={() => { setActiveTab("history"); loadBuildingHistory(); }}
          style={{
            padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
            border: "none", borderBottom: activeTab === "history" ? "3px solid #1976d2" : "3px solid transparent",
            background: "none", color: activeTab === "history" ? "#1976d2" : "#666",
          }}
        >Building History {buildingHistory ? `(${buildingHistory.total})` : ""}</button>
      </div>

      {activeTab === "comps" && <>
      {/* Insufficient comps warning */}
      {data.cumulative_score < 700 && (
        <div style={{
          padding: "10px 14px", borderRadius: 6, marginBottom: 12,
          borderLeft: "4px solid #c62828", background: "#ffebee",
          fontSize: 13, color: "#333",
        }}>
          <strong style={{ color: "#c62828" }}>⚠ Insufficient comparable data</strong>
          {" — "}only {data.total_comps} qualifying comps found (score {data.cumulative_score}/700).
          AI prediction may be less reliable. Review manually.
        </div>
      )}

      {/* Possible duplicate comps warning */}
      {duplicateFlags.length > 0 && (
        <div style={{
          padding: "10px 14px", borderRadius: 6, marginBottom: 12,
          borderLeft: "4px solid #f57c00", background: "#fff3e0",
          fontSize: 13, color: "#333",
        }}>
          <strong style={{ color: "#e65100" }}>⚠ Possible duplicate comps</strong>
          {" — "}some comps look like the same unit listed twice (e.g. a transposed unit number)
          within 90 days. Verify and exclude any duplicates below.
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            {duplicateFlags.map((f, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <strong>{f.address}</strong>
                {f.unit_labels?.length > 0 && <> — units {f.unit_labels.map(u => `#${u}`).join(" / ")}</>}
                {": "}
                {f.comps.map((c, j) => (
                  <span key={c.listing_id}>
                    {j > 0 && " vs "}
                    ${c.rent?.toLocaleString()}{c.date ? ` (${String(c.date).slice(0, 10)})` : ""}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tier legend */}
      <div style={{ display: "flex", gap: 12, marginBottom: 12, fontSize: 11 }}>
        {Object.entries(TIER_LABELS).map(([tier, label]) => (
          <span key={tier} style={{ background: TIER_COLORS[tier], padding: "2px 8px", borderRadius: 4, border: "1px solid #ddd" }}>
            T{tier}: {label}
          </span>
        ))}
      </div>

      {/* Same-unit rental history anchor */}
      {data.unit_history?.length > 0 && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "#e8f5e9", borderRadius: 6, borderLeft: "4px solid #4caf50" }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: "#2e7d32" }}>
            Same Unit Rental History
          </div>
          {data.unit_history.map((uh, i) => (
            <div key={i} style={{ display: "flex", gap: 16, fontSize: 13, padding: "4px 0", alignItems: "center" }}>
              <span><strong>${uh.rent?.toLocaleString()}/mo</strong></span>
              <span>{uh.bedrooms}BR / {uh.bathrooms}BA</span>
              {uh.sqft && <span>{uh.sqft.toLocaleString()} sqft</span>}
              <span style={{
                fontSize: 11, padding: "1px 6px", borderRadius: 3,
                background: uh.status === "RENTED" || uh.status === "RENTED THRU US" ? "#c8e6c9"
                  : uh.status === "ACTIVE" ? "#fff9c4" : "#ffcdd2",
                color: uh.status === "ACTIVE" || uh.status === "RENTED" || uh.status === "RENTED THRU US" ? "#333" : "#c62828",
              }}>
                {uh.status === "DELETE" || uh.status === "DELETED" ? "Delisted"
                  : uh.status === "RENTED THRU US" ? "Rented" : uh.status}
              </span>
              <span style={{ fontSize: 11, color: "#666" }}>{(uh.closing_date || uh.listed_at)?.slice(0, 10) || ""}</span>
              {uh.listing_url && (
                <a href={uh.listing_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1976d2" }}>View</a>
              )}
            </div>
          ))}
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "auto" }}>
        <thead>
          <tr>
            <th style={sortableThStyle} onClick={() => toggleSort("rank")}>#{ sortIcon("rank")}</th>
            <th style={thStyle}>Address</th>
            <th style={sortableThStyle} onClick={() => toggleSort("rent")}>Rent{sortIcon("rent")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("sqft")}>Sqft{sortIcon("sqft")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("reno")}>Reno{sortIcon("reno")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("floor")}>Floor{sortIcon("floor")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("dw")}>DW{sortIcon("dw")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("wd")}>W/D{sortIcon("wd")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("out")}>Out{sortIcon("out")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("score")}>Score{sortIcon("score")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("dist")}>Dist{sortIcon("dist")}</th>
            <th style={thStyle}>Status</th>
            <th style={sortableThStyle} onClick={() => toggleSort("date")}>Date{sortIcon("date")}</th>
            <th style={sortableThStyle} onClick={() => toggleSort("dom")}>DOM{sortIcon("dom")}</th>
            <th style={{ ...thStyle, minWidth: 120 }}>Deductions</th>
            <th style={{ ...thStyle, width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {/* Target reference row */}
          <tr style={{ background: "#e0e0e0", borderBottom: "2px solid #999", fontWeight: 600, fontSize: 12 }}>
            <td style={tdStyle}></td>
            <td style={{ ...tdStyle, color: "#555" }}>TARGET</td>
            <td style={tdStyle}>${target?.price?.toLocaleString()}</td>
            <td style={tdStyle}>{sf.sqft?.toLocaleString() || "-"}</td>
            <td style={tdStyle}>{sf.renovation ?? "-"}</td>
            <td style={tdStyle}>{sf.floor ?? "-"}</td>
            <td style={tdStyle}>{sf.dishwasher ? "Y" : "N"}</td>
            <td style={tdStyle}>{formatWD(sf.washer_dryer)}</td>
            <td style={tdStyle}>{sf.outdoor_space ? "Y" : "N"}</td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
          </tr>
          {comps.filter(c => !c.excluded).map((comp, displayIdx) => {
            const runningTotal = comps
              .filter(c => !c.excluded)
              .slice(0, displayIdx + 1)
              .reduce((sum, c) => sum + c.similarity_score, 0);

            return (
              <tr
                key={comp.listing_id}
                style={{
                  background: TIER_COLORS[comp.tier] || "#fff",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <td style={tdStyle}>{comp.rank}</td>
                <td style={tdStyle}>
                  {comp.listing_url ? (
                    <a href={comp.listing_url} target="_blank" rel="noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
                      {comp.address?.trim()}
                    </a>
                  ) : comp.address?.trim()}
                  {comp.property_id && (
                    <a href={`/buildings/${comp.property_id}/edit`} target="_blank" rel="noopener noreferrer"
                       style={{ marginLeft: 4, fontSize: 12, textDecoration: "none", opacity: 0.5 }}
                       title="Open building"
                       onClick={(e) => e.stopPropagation()}>&#127963;</a>
                  )}
                  {comp.is_same_building && (
                    <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 6, padding: "1px 6px", borderRadius: 3, background: "#2196f3", color: "#fff" }}>
                      SAME
                    </span>
                  )}
                  {comp.verified && (
                    <span title="Verified listing" style={{ fontSize: 10, fontWeight: 600, marginLeft: 4, padding: "1px 6px", borderRadius: 3, background: "#4caf50", color: "#fff" }}>
                      V
                    </span>
                  )}
                  {duplicateIds.has(comp.listing_id) && (
                    <span title="Possible duplicate of another comp (same unit, within 90 days) — verify and exclude if so"
                      style={{ fontSize: 10, fontWeight: 600, marginLeft: 4, padding: "1px 6px", borderRadius: 3, background: "#f57c00", color: "#fff" }}>
                      DUP
                    </span>
                  )}
                  {onEditComp && (
                    <span
                      title="Edit rental listing"
                      onClick={(e) => { e.stopPropagation(); onEditComp(comp.listing_id); }}
                      style={{ fontSize: 13, marginLeft: 6, cursor: "pointer", opacity: 0.5 }}
                    >&#9998;</span>
                  )}
                </td>
                <td style={{ ...tdStyle, fontWeight: 600 }} title={comp.net_effective_rent ? `Ask: $${comp.ask_rent?.toLocaleString()} | ${comp.months_free} mo free | ${comp.lease_term_months}mo lease` : undefined}>
                  ${comp.rent?.toLocaleString()}{comp.net_effective_rent ? "*" : ""}
                </td>
                <td style={featureCell("sqft", comp)}>{comp.sqft?.toLocaleString() || "-"}</td>
                <td style={featureCell("renovation", comp)}>{comp.scoring_fields?.renovation ?? "-"}</td>
                <td style={featureCell("floor", comp)}>{comp.scoring_fields?.floor ?? "-"}</td>
                <td style={featureCell("dishwasher", comp)}>{comp.scoring_fields?.dishwasher ? "Y" : "N"}</td>
                <td style={featureCell("washer_dryer", comp)}>{formatWD(comp.scoring_fields?.washer_dryer)}</td>
                <td style={featureCell("outdoor_space", comp)}>{comp.scoring_fields?.outdoor_space ? "Y" : "N"}</td>
                <td style={{ ...tdStyle, background: comp.similarity_score >= 70 ? "#c8e6c9" : "#ffcdd2", fontWeight: 700 }}>
                  {comp.similarity_score}
                  <span style={{ fontSize: 10, color: "#666", marginLeft: 3 }}>({runningTotal})</span>
                </td>
                <td style={tdStyle}>{comp.is_same_building ? "Same" : `${comp.distance_miles}mi`}</td>
                <td style={tdStyle}>
                  <span style={{
                    fontSize: 11, padding: "1px 6px", borderRadius: 3,
                    background: comp.status === "RENTED" || comp.status === "RENTED THRU US" ? "#c8e6c9"
                      : comp.status === "ACTIVE" ? "#fff9c4"
                      : "#ffcdd2",
                    color: comp.status === "ACTIVE" || comp.status === "RENTED" || comp.status === "RENTED THRU US" ? "#333" : "#c62828",
                  }}>
                    {comp.status === "DELETE" || comp.status === "DELETED" ? "Delisted"
                      : comp.status === "RENTED THRU US" ? "Rented"
                      : comp.status}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontSize: 11, whiteSpace: "nowrap" }}>
                  {(comp.closing_date || comp.listed_at)?.slice(0, 10) || "-"}
                </td>
                <td style={{ ...tdStyle, fontSize: 11 }}>
                  {comp.active_dom != null ? comp.active_dom : "-"}
                </td>
                <td style={tdStyle}>
                  {comp.differences?.filter(d => d.similarity_deduction !== 0).length === 0 ? (
                    <span style={{ color: "#2e7d32", fontSize: 12 }}>Perfect match</span>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                      {comp.differences?.filter(d => d.similarity_deduction !== 0).map((d, i) => (
                        <span key={i} title={d.description} style={{
                          fontSize: 10, padding: "1px 4px", borderRadius: 3, cursor: "help", whiteSpace: "nowrap",
                          background: attrDelta(d.attribute, comp) > 0 ? "#ffcdd2" : "#c8e6c9",
                        }}>
                          {d.attribute}: {d.similarity_deduction}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {onExcludeComp && (
                    <span
                      title="Exclude this comp"
                      onClick={() => onExcludeComp(comp.listing_id)}
                      style={{ cursor: "pointer", fontSize: 12, color: "#d32f2f", opacity: 0.5 }}
                    >&times;</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Excluded comps */}
      {comps.some(c => c.excluded) && (
        <div style={{ marginTop: 12, padding: "8px 12px", background: "#fff3e0", borderRadius: 6, fontSize: 12 }}>
          <strong style={{ color: "#e65100" }}>Excluded comps ({comps.filter(c => c.excluded).length}):</strong>
          {comps.filter(c => c.excluded).map(comp => (
            <span key={comp.listing_id} style={{ marginLeft: 8 }}>
              {comp.address?.trim()} (${comp.rent?.toLocaleString()})
              {onExcludeComp && (
                <span
                  onClick={() => onExcludeComp(comp.listing_id, false)}
                  style={{ cursor: "pointer", color: "#2e7d32", marginLeft: 4, fontWeight: 600 }}
                  title="Re-include this comp"
                >+</span>
              )}
            </span>
          ))}
        </div>
      )}
      </>}

      {/* Building History Tab */}
      {activeTab === "history" && (
        <div>
          {historyLoading && <div style={{ padding: 20, textAlign: "center", color: "#666" }}>Loading building history...</div>}
          {buildingHistory && !historyLoading && (
            buildingHistory.total === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#666" }}>No rental history found for this building.</div>
            ) : (
              <BuildingHistoryContent history={buildingHistory.history} thStyle={thStyle} tdStyle={tdStyle} formatWD={formatWD} onEditComp={onEditComp} />
            )
          )}
        </div>
      )}
    </div>
  );
};

const CacheInfo = ({ cache, aiGeneratedAt, compsUpdatedAt, onShowHistory }) => {
  const fmt = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? "—" : d.toLocaleString();
  };
  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    fontSize: "12px",
    color: "#666",
  };

  const headingStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  };

  return (
    <div style={sectionStyle}>
      <h3 style={headingStyle}>Cache Information</h3>
      <p>ML prediction generated: {fmt(cache?.generated_at)}</p>
      <p>Comps last updated: {fmt(compsUpdatedAt)}</p>
      <p>AI analysis run: {fmt(aiGeneratedAt)}</p>
      <p>Cached: {cache?.is_cached ? "Yes" : "No"}</p>
      <p>Total Comparables Found: {cache?.total_comps_found}</p>
      {onShowHistory && (
        <button
          onClick={onShowHistory}
          style={{ marginTop: "8px", fontSize: "12px", padding: "5px 10px", cursor: "pointer",
                   background: "#0d47a1", color: "#fff", border: "none", borderRadius: "4px" }}>
          AI run history
        </button>
      )}
    </div>
  );
};

// Lazy modal: past AI prediction runs (date · rent + Δ vs prior · reasoning · key factors)
const AiHistoryModal = ({ loading, history, onClose }) => {
  const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? "—" : d.toLocaleString();
  };
  const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
  const overlay = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  const box = {
    background: "#fff", borderRadius: "8px", width: "min(720px, 92vw)",
    maxHeight: "85vh", overflowY: "auto", padding: "20px", boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
  };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
            AI run history{Array.isArray(history) ? ` (${history.length})` : ""}
          </h3>
          <span onClick={onClose} style={{ cursor: "pointer", fontSize: "20px", color: "#666" }}>&times;</span>
        </div>
        {loading && <p style={{ color: "#666" }}>Loading…</p>}
        {!loading && (!history || history.length === 0) && (
          <p style={{ color: "#666" }}>No AI runs recorded yet — runs are captured from here on.</p>
        )}
        {!loading && history && history.map((run, i) => {
          const prior = history[i + 1];
          const delta = prior && run.predicted_rent != null && prior.predicted_rent != null
            ? run.predicted_rent - prior.predicted_rent : null;
          const deltaStr = delta == null ? "" : (delta === 0 ? " (—)"
            : delta > 0 ? ` (▲ $${Math.abs(delta).toLocaleString()})` : ` (▼ $${Math.abs(delta).toLocaleString()})`);
          const deltaColor = delta == null || delta === 0 ? "#666" : delta > 0 ? "#28a745" : "#dc3545";
          return (
            <div key={run.id || i} style={{ borderTop: i === 0 ? "none" : "1px solid #eee", padding: "10px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#666" }}>{fmtDate(run.created_at)}{run.model ? ` · ${run.model}` : ""}</span>
                <span style={{ fontWeight: 700 }}>
                  {money(run.predicted_rent)}<span style={{ color: deltaColor, fontWeight: 600 }}>{deltaStr}</span>
                </span>
              </div>
              {(run.rent_low != null || run.rent_high != null || run.confidence) && (
                <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                  Range {money(run.rent_low)}–{money(run.rent_high)}{run.confidence ? ` · ${run.confidence} confidence` : ""}
                </div>
              )}
              {run.reasoning && <div style={{ fontSize: "13px", marginTop: "4px" }}>{run.reasoning}</div>}
              {Array.isArray(run.key_factors) && run.key_factors.length > 0 && (
                <ul style={{ fontSize: "12px", color: "#444", margin: "4px 0 0", paddingLeft: "18px" }}>
                  {run.key_factors.map((f, k) => <li key={k}>{typeof f === "string" ? f : JSON.stringify(f)}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};